
import React, { useState, useEffect, useRef, useMemo, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { User, Company, Lead, UserRole, Task, UserFeaturesConfig } from '../types';
import { api, exportToCSV } from '../services/apiService';
import LeadDetailsModal from '../components/LeadDetailsModal';
import SelectField from '../components/SelectField';
import AddLeadModal from '../components/AddLeadModal';
import { toast } from '../services/toast';
// Vues / modales secondaires en lazy-load — réduisent le bundle initial.
const ImportLeadsModal  = lazy(() => import('../components/ImportLeadsModal'));
const CalendarView      = lazy(() => import('../components/CalendarView'));
const OpportunitiesView = lazy(() => import('./OpportunitiesView'));
import TasksView from './TasksView';
import TaskWidget from '../components/TaskWidget';
import { ResponsiveContainer, Tooltip as RechartsTooltip, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { DEFAULT_REVENUE_COLORS, getCompanyStatuses, findStatus, statusDotStyle, statusBadgeStyle } from '../constants';
import { displayEmail, displayPhone } from '../utils/maskContact';
import { hasPermission, CustomRole } from '../utils/permissions';
import { featureEnabled } from '../components/UserFeaturesTab';
import { getEffectiveConfig, DEFAULT_FIELDS } from '../components/CardConfigTab';

interface CompanyDashboardProps {
  user: User;
  company: Company;
  activeView: string;
}

const getAdaptiveDashboardColumnCount = (containerWidth: number): 1 | 2 | 3 | 4 | 5 | 6 => {
  if (containerWidth < 640)  return 1;
  if (containerWidth < 1100) return 2;
  if (containerWidth < 1500) return 3;
  if (containerWidth < 2500) return 4;
  if (containerWidth < 3000) return 5;
  return 6;
};

type DashboardWidgetDef = { id: string; label: string; icon: string; colSpan: 1 | 2 | 3 | 4 | 5 | 6; row: number; col: number; visible: boolean };

/**
 * Résout les chevauchements entre widgets pour le rendu.
 * Chaque widget conserve sa position préférée si libre, sinon est décalé vers
 * la prochaine cellule disponible (lecture gauche→droite, haut→bas).
 * Ne modifie pas le layout sauvegardé.
 */
function resolveOverlaps(widgets: DashboardWidgetDef[], cols: number): DashboardWidgetDef[] {
  const sorted = [...widgets].sort((a, b) => a.row - b.row || a.col - b.col);
  const occupied = new Set<string>();
  const result: DashboardWidgetDef[] = [];

  const isFree = (r: number, c: number, span: number): boolean => {
    for (let dc = 0; dc < span; dc++) {
      if (c + dc >= cols || occupied.has(`${r}-${c + dc}`)) return false;
    }
    return true;
  };

  for (const w of sorted) {
    const span = Math.min(w.colSpan, cols) as DashboardWidgetDef['colSpan'];
    let row = w.row;
    let col = Math.min(w.col, Math.max(0, cols - span));

    if (!isFree(row, col, span)) {
      let found = false;
      outer: for (let r = 0; r <= row + 20; r++) {
        for (let c = 0; c <= cols - span; c++) {
          if (isFree(r, c, span)) { row = r; col = c; found = true; break outer; }
        }
      }
      if (!found) { row = sorted.length; col = 0; } // fallback: nouvelle ligne
    }

    for (let dc = 0; dc < span; dc++) occupied.add(`${row}-${col + dc}`);
    result.push({ ...w, row, col, colSpan: span });
  }
  return result;
}

/**
 * Réorganisation automatique : compacte tous les widgets visibles ligne par ligne
 * sans laisser de trous, en respectant leur colSpan courant.
 */
function autoArrangeLayout(widgets: DashboardWidgetDef[], cols: number): DashboardWidgetDef[] {
  const visible = [...widgets.filter(w => w.visible)].sort((a, b) => a.row - b.row || a.col - b.col);
  const occupied = new Set<string>();
  const result: DashboardWidgetDef[] = [];

  const isFree = (r: number, c: number, span: number): boolean => {
    for (let dc = 0; dc < span; dc++) {
      if (c + dc >= cols || occupied.has(`${r}-${c + dc}`)) return false;
    }
    return true;
  };

  const findNext = (span: number): { r: number; c: number } => {
    for (let r = 0; r < 200; r++) {
      for (let c = 0; c <= cols - span; c++) {
        if (isFree(r, c, span)) return { r, c };
      }
    }
    return { r: 0, c: 0 };
  };

  for (const w of visible) {
    const span = Math.min(w.colSpan, cols) as DashboardWidgetDef['colSpan'];
    const { r, c } = findNext(span);
    for (let dc = 0; dc < span; dc++) occupied.add(`${r}-${c + dc}`);
    result.push({ ...w, row: r, col: c, colSpan: span });
  }

  return [...result, ...widgets.filter(w => !w.visible)];
}

// ---- Layouts multi-résolution (un layout par nombre de colonnes) ----
type ByColsLayouts = Record<number, any[]>;

// Déduit le nombre de colonnes pour lequel un layout "plat" (ancien format) a été pensé.
function inferLayoutCols(layout: any[]): number {
  if (!Array.isArray(layout) || layout.length === 0) return 4;
  let max = 0;
  for (const w of layout) {
    if (!w || w.visible === false || w.id === 'kpis') continue;
    const col = Number(w.col) || 0;
    const span = Number(w.colSpan) || 1;
    if (col + span > max) max = col + span;
  }
  return max > 0 ? max : 4;
}

// Convertit n'importe quel format stocké (ancien tableau plat OU nouveau { byCols }) en map.
function normalizeStoredLayout(stored: any): ByColsLayouts {
  if (!stored) return {};
  if (typeof stored === 'object' && !Array.isArray(stored) && stored.byCols && typeof stored.byCols === 'object') {
    const out: ByColsLayouts = {};
    for (const [k, v] of Object.entries(stored.byCols)) {
      const n = Number(k);
      if (Number.isFinite(n) && n > 0 && Array.isArray(v)) out[n] = v as any[];
    }
    return out;
  }
  if (Array.isArray(stored)) {
    return { [inferLayoutCols(stored)]: stored };
  }
  return {};
}

// Retourne le layout pour `cols` : exact si présent, sinon prend le plus proche
// et l'auto-arrange dans la nouvelle grille (préserve l'ordre de lecture).
function pickLayoutForCols(byCols: ByColsLayouts, cols: number, fallback: any[]): any[] {
  const direct = byCols[cols];
  if (Array.isArray(direct) && direct.length > 0) return direct;
  const available = Object.keys(byCols)
    .map(Number)
    .filter(n => Number.isFinite(n) && n > 0 && Array.isArray(byCols[n]) && byCols[n].length > 0);
  if (available.length === 0) return autoArrangeLayout(fallback as DashboardWidgetDef[], cols);
  available.sort((a, b) => Math.abs(a - cols) - Math.abs(b - cols) || b - a);
  const source = byCols[available[0]];
  return autoArrangeLayout(source as DashboardWidgetDef[], cols);
}

const CompanyDashboard: React.FC<CompanyDashboardProps> = ({ user, company, activeView }) => {
  // Statuts effectifs pour cette entreprise
  const statuses = getCompanyStatuses(company);

  // Détection thème clair/sombre (réactif)
  const [isLight, setIsLight] = useState(() => document.documentElement.classList.contains('light'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsLight(document.documentElement.classList.contains('light'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const cardBg = (isLight ? company.cardBgColorLight : company.cardBgColor) || undefined;
  const cardName = (isLight ? company.cardNameColorLight : company.cardNameColor) || undefined;
  const cardEmail = (isLight ? company.cardEmailColorLight : company.cardEmailColor) || undefined;

  // Couleurs effectives : config entreprise ou couleurs par défaut
  const effectiveRevenueColors = company.revenueColors && Object.keys(company.revenueColors).length > 0
    ? company.revenueColors
    : DEFAULT_REVENUE_COLORS;

  const [leads, setLeads] = useState<Lead[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);

  // Recherche globale : si un lead a été choisi depuis la barre de recherche,
  // App.tsx pose son ID dans sessionStorage ET émet l'event `orion:open-lead`.
  // On écoute les deux pour ouvrir la fiche, y compris quand on est déjà sur
  // la vue prospects (cartes/kanban) — dans ce cas `leads` ne change pas, donc
  // le seul useEffect([leads]) ne suffit pas.
  useEffect(() => {
    const tryOpenPending = () => {
      const pendingId = sessionStorage.getItem('orion_open_lead_id');
      if (!pendingId || leads.length === 0) return;
      const lead = leads.find(l => String(l.id) === String(pendingId));
      if (lead) {
        setSelectedLead(lead);
        sessionStorage.removeItem('orion_open_lead_id');
      }
    };
    tryOpenPending();
    window.addEventListener('orion:open-lead', tryOpenPending);
    return () => window.removeEventListener('orion:open-lead', tryOpenPending);
  }, [leads]);

  // Pagination par statut : 10 vignettes affichées par défaut sur chaque
  // colonne (Favoris, Nouveau, Contacter, …), avec boutons "Afficher plus"
  // (+10) et "Afficher moins" (-10). Identique pour kanban et cards.
  // Reset à 10 quand un filtre / la recherche change.
  const LEADS_PAGE_SIZE = 10;
  const [cardsShownByCol, setCardsShownByCol] = useState<Record<string, number>>({});
  const [kanbanShownByCol, setKanbanShownByCol] = useState<Record<string, number>>({});

  // Filtres — persistants via localStorage
  const storedFilters = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('orion_filters') || '{}'); } catch { return {}; }
  }, []);
  const [filterAgent, setFilterAgent] = useState<string>(storedFilters.agent || 'all');
  const [filterStatus, setFilterStatus] = useState<string>(storedFilters.status || 'all');
  const [filterSector, setFilterSector] = useState<string>(storedFilters.sector || 'all');
  const [filterColor, setFilterColor] = useState<string>(storedFilters.color || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTag, setSearchTag] = useState('');
  // Période d'analyse du dashboard (jours). 0 = tout l'historique.
  // Appliqué UNIQUEMENT dans la vue Dashboard via filteredLeads (les vues
  // Leads/Kanban/Cartes gardent leur comportement pleine liste).
  const [dashboardPeriodDays, setDashboardPeriodDays] = useState<number>(() => {
    const v = Number(localStorage.getItem('orion_dashboard_period'));
    return Number.isFinite(v) ? v : 30;
  });
  useEffect(() => {
    localStorage.setItem('orion_dashboard_period', String(dashboardPeriodDays));
  }, [dashboardPeriodDays]);

  useEffect(() => {
    localStorage.setItem('orion_filters', JSON.stringify({ agent: filterAgent, status: filterStatus, sector: filterSector, color: filterColor }));
  }, [filterAgent, filterStatus, filterSector, filterColor]);

  // Quand les filtres ou la recherche changent, on réinitialise les compteurs
  // de pagination des vues Cards/Kanban pour repartir des 20 premiers.
  useEffect(() => {
    setCardsShownByCol({});
    setKanbanShownByCol({});
  }, [filterAgent, filterStatus, filterSector, filterColor, searchTerm, searchTag]);

  // Tri
  const [sortConfig, setSortConfig] = useState<{ key: keyof Lead | 'revenue'; direction: 'asc' | 'desc' } | null>(null);

  // Toggle vue liste / cards / kanban pour la page Prospects
  // Sur mobile (< 768px), on défaut sur cards si aucune préférence sauvegardée
  const [leadsViewMode, setLeadsViewMode] = useState<'list' | 'cards' | 'kanban'>(
    (localStorage.getItem('orion_view_mode') as 'list' | 'cards' | 'kanban') ||
    (window.innerWidth < 768 ? 'cards' : 'list')
  );

  // Kanban drag & drop
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Sélection en masse
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [bulkStatusValue, setBulkStatusValue] = useState<string>('');
  const [bulkAssignValue, setBulkAssignValue] = useState<string>('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Fonctionnalités utilisateurs — les admins/managers ont toujours tout
  // Résolution : user-level (override individuel) → company-level → defaultOn
  const isRestrictedUser = user.role === UserRole.USER;
  const resolveFeature = (key: keyof UserFeaturesConfig, defaultOn: boolean): boolean => {
    if (!isRestrictedUser) return true;
    // Override individuel défini → il prime
    const userUf = user.userFeatures as Record<string, boolean | undefined> | undefined;
    if (userUf?.[key as string] !== undefined) return !!userUf[key as string];
    // Sinon on tombe sur le paramètre company
    return featureEnabled(company.userFeatures, key, defaultOn);
  };
  const canViewSwitch        = resolveFeature('viewSwitch',        true);
  const canSearch            = resolveFeature('search',            true);
  const canTagSearch         = resolveFeature('tagSearch',         true);
  const canFilters           = resolveFeature('filters',           true);
  const canColorFilter       = resolveFeature('colorFilter',       true);
  const canImportCsv         = resolveFeature('importCsv',         false);
  const canDashboardCustomize= resolveFeature('dashboardCustomize', false);

  // Profil de permissions custom de l'utilisateur courant — étend les droits
  // au-delà du rôle de base. Chargé une fois et utilisé via hasPermission().
  const [currentUserCustomRole, setCurrentUserCustomRole] = useState<CustomRole | null>(null);
  useEffect(() => {
    if (!user.customRoleId) { setCurrentUserCustomRole(null); return; }
    let cancelled = false;
    api.getCustomRoles().then(list => {
      if (cancelled) return;
      const r = (list as CustomRole[]).find(x => x.id === user.customRoleId) || null;
      setCurrentUserCustomRole(r);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user.customRoleId]);

  // canExportCsv : on respecte d'abord le profil de permissions custom (option C
  // future-proof B), puis on retombe sur l'ancien UserFeatures pour compatibilité.
  const canExportCsv = hasPermission(user, 'leads.exportCsv', currentUserCustomRole)
                       || resolveFeature('exportCsv', false);
  // Création / modification de prospects — défauts à true (préserve l'existant).
  const canCreateLead = hasPermission(user, 'leads.create', currentUserCustomRole);
  const canModifyLead = hasPermission(user, 'leads.modify', currentUserCustomRole);

  // Configuration des cartes (champs visibles, ordre, appel rapide)
  const cardConfig = getEffectiveConfig(company);

  const [currencySymbol, setCurrencySymbol] = useState<string>(company.currency || '€');

  useEffect(() => {
    api.getSystemConfig().then(config => {
      const code = company.currency || 'EUR';
      const found = config.currencies?.find((c: { code: string }) => c.code === code);
      setCurrencySymbol(found?.symbol ?? code);
    }).catch(() => {});
  }, [company.currency]);

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportAgentId, setExportAgentId] = useState<string>('all');
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const exportBtnRef = useRef<HTMLButtonElement>(null);
  const exportPanelRef = useRef<HTMLDivElement>(null);
  const [exportMenuRect, setExportMenuRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const fetchData = async (attempt = 0) => {
      if (cancelled) return;
      setLoading(true);

      const [leadsResult, usersResult, tasksResult] = await Promise.allSettled([
        api.getLeads({ companyId: company.id, userId: user.id, role: user.role }),
        api.getCompanyUsers(company.id),
        api.getTasks({ companyId: company.id })
      ]);

      if (cancelled) return;

      let shouldRetry = false;

      if (leadsResult.status === 'fulfilled') {
        setLeads(leadsResult.value || []);
      } else {
        console.error('Dashboard leads fetch error', leadsResult.reason);
        shouldRetry = true;
      }

      if (usersResult.status === 'fulfilled') {
        const companyUsers = usersResult.value || [];
        if (
          user.role === UserRole.COMPANY_ADMIN &&
          !companyUsers.some(x => String(x.id) === String(user.id))
        ) {
          setUsers([user, ...companyUsers]);
        } else {
          setUsers(companyUsers);
        }
      } else {
        console.error('Dashboard users fetch error', usersResult.reason);
        shouldRetry = true;
      }

      if (tasksResult.status === 'fulfilled') {
        setTasks(tasksResult.value || []);
      } else {
        console.error('Dashboard tasks fetch error', tasksResult.reason);
        shouldRetry = true;
      }

      try {
        await api.checkUpcomingTaskReminders(user.id);
      } catch (e) {
        console.warn('Task reminder check failed');
      }

      if (shouldRetry && attempt === 0 && !cancelled) {
        retryTimer = setTimeout(() => {
          void fetchData(1);
        }, 400);
        return;
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    void fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inBtn = exportBtnRef.current?.contains(target);
      const inPanel = exportPanelRef.current?.contains(target);
      if (!inBtn && !inPanel) setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // UTILISATION DE user.id AU LIEU DE user POUR ÉVITER LES BOUCLES
  }, [company.id, user.id, user.role, activeView]);

  const handleAddLead = async (leadData: Omit<Lead, 'id' | 'createdAt'>) => {
    const tempId = 'lead_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    const createdAt = new Date().toISOString();

    // assignedTo : on transmet la valeur de l'utilisateur telle quelle (peut être
    // vide s'il a choisi "Non assigné"). Le backend déclenche autoAssignLead si
    // vide ; la réponse contient l'assigné final que l'on applique au lead local.
    const requestedAssignedTo = String(leadData.assignedTo ?? '');

    const optimisticLead: Lead = {
      id: tempId,
      companyId: company.id,
      clientCompanyName: leadData.clientCompanyName || '',
      tag: leadData.tag || '',
      sectorActivity: leadData.sectorActivity || '',
      name: leadData.name || 'Nouveau Prospect',
      email: leadData.email || '',
      phone: leadData.phone || '',
      phoneLandline: leadData.phoneLandline || '',
      status: leadData.status || 'new',
      // Pour l'affichage immédiat on garde la valeur demandée (peut être vide,
      // la fiche apparaîtra dans "non affectés" en attendant la réponse serveur).
      assignedTo: requestedAssignedTo,
      createdAt: createdAt,
      potentialRevenue: leadData.potentialRevenue || '',
      additionalInfo: leadData.additionalInfo || '',
      isFavorite: leadData.isFavorite || false,
      source: leadData.source || '',
      customFields: leadData.customFields || {},
    };

    setLeads(prevLeads => [optimisticLead, ...prevLeads]);
    setSearchTerm('');

    try {
        const confirmedLead = await api.createLead({
            ...optimisticLead,
            id: tempId,
            assignedTo: requestedAssignedTo, // explicite : ne pas écraser avec user.id
            // Permet au call-site (modale d'ajout) de transmettre la confirmation utilisateur.
            ...((leadData as any).forceDuplicate ? { forceDuplicate: true } : {}),
        } as any);
        if (confirmedLead) {
             setLeads(prevLeads => prevLeads.map(l => l.id === tempId ? confirmedLead : l));
        }
    } catch (e: any) {
        console.error("Erreur de confirmation API", e);
        setLeads(prevLeads => prevLeads.filter(l => l.id !== tempId));
        // On RE-LANCE l'erreur — la modale appelante affichera un message ciblé
        // (doublon, validation, etc.) et restera ouverte pour permettre la correction.
        throw e;
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await api.getLeads({
        companyId: company.id, 
        userId: user.id, 
        role: user.role, 
        agentId: exportAgentId === 'all' ? undefined : exportAgentId,
        dateFrom: exportDateFrom, 
        dateTo: exportDateTo
      });
      
      const formatted = dataToExport.map(l => ({
        ID: l.id, 
        Nom: l.name, 
        Email: l.email, 
        Statut: l.status, 
        Agent: users.find(u => String(u.id) === String(l.assignedTo))?.name || 'Libre',
        Date: l.createdAt
      }));
      
      exportToCSV(formatted, `export_orion_${company.name}.csv`);
      setShowExportMenu(false);
    } finally {
      setIsExporting(false);
    }
  };

  const csvInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);

  // L'utilisateur sélectionne un fichier → on ouvre la modale de mapping ;
  // c'est elle qui appellera handleImportRows pour créer les leads.
  const handleImportFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const handleImportRows = async (rows: { name: string; email?: string; phone?: string; phoneLandline?: string; clientCompanyName?: string; sectorActivity?: string; tag?: string; potentialRevenue?: string; source?: string; additionalInfo?: string; customFields?: Record<string, any> }[]) => {
    let imported = 0;
    const skipped: { line: number; name: string; reason: string }[] = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      try {
        await api.createLead({
          companyId: company.id,
          name: r.name,
          email: r.email || '',
          phone: r.phone || '',
          phoneLandline: r.phoneLandline || '',
          clientCompanyName: r.clientCompanyName || '',
          sectorActivity: r.sectorActivity || '',
          tag: r.tag || '',
          potentialRevenue: r.potentialRevenue || '',
          source: r.source || '',
          additionalInfo: r.additionalInfo || '',
          ...(r.customFields ? { customFields: r.customFields } : {}),
          status: 'new',
          assignedTo: user.id,
        } as any, { id: user.id, name: user.name });
        imported++;
      } catch (err: any) {
        const msg = err?.message || String(err);
        const reason = msg.startsWith('DUPLICATE:') ? 'doublon' : msg;
        skipped.push({ line: i + 2, name: r.name, reason });
      }
    }
    const freshLeads = await api.getLeads({ companyId: company.id, userId: user.id, role: user.role });
    setLeads(freshLeads);
    return { imported, skipped };
  };

  // (Ancien handler CSV mono-bloc supprimé — remplacé par ImportLeadsModal
  // avec mapping de colonnes et support XLSX.)
  const handleLeadUpdate = (updatedLead: Lead) => {
    setLeads(prevLeads => prevLeads.map(l => String(l.id) === String(updatedLead.id) ? updatedLead : l));
    if (selectedLead && String(selectedLead.id) === String(updatedLead.id)) {
      setSelectedLead(updatedLead);
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      console.log('[DELETE] Start deleting lead:', leadId);
      await api.deleteLead(leadId);
      console.log('[DELETE] api.deleteLead resolved OK');
      setLeads(prev => {
        const after = prev.filter(l => String(l.id) !== String(leadId));
        console.log('[DELETE] leads before:', prev.length, 'after:', after.length);
        return after;
      });
      setSelectedLead(null);
      console.log('[DELETE] Done');
    } catch (err) {
      console.error('[DELETE] Error:', err);
      toast.error('Erreur suppression: ' + (err instanceof Error ? err.message : String(err)));
    }
  };

  // === Actions en masse ===
  const toggleLeadSelection = (leadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Supprimer ${selectedLeadIds.size} prospect(s) ? Cette action est irréversible.`)) return;
    setBulkProcessing(true);
    try {
      await api.bulkDeleteLeads(Array.from(selectedLeadIds));
      setLeads(prev => prev.filter(l => !selectedLeadIds.has(l.id)));
      setSelectedLeadIds(new Set());
    } catch (err) {
      console.error('Erreur suppression en masse:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkStatusChange = async (status: string) => {
    setBulkProcessing(true);
    try {
      await api.bulkUpdateLeads(Array.from(selectedLeadIds), 'status', status);
      setLeads(prev => prev.map(l => selectedLeadIds.has(l.id) ? { ...l, status: status as Lead['status'] } : l));
      setSelectedLeadIds(new Set());
    } catch (err) {
      console.error('Erreur maj statut en masse:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkReassign = async (userId: string) => {
    setBulkProcessing(true);
    try {
      await api.bulkUpdateLeads(Array.from(selectedLeadIds), 'assigned_to', userId);
      setLeads(prev => prev.map(l => selectedLeadIds.has(l.id) ? { ...l, assignedTo: userId } : l));
      setSelectedLeadIds(new Set());
    } catch (err) {
      console.error('Erreur réassignation en masse:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleSort = (key: keyof Lead | 'revenue') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const formatDateShort = (dateStr: string) => {
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  // Agents visibles : MANAGER ne voit que son équipe, ADMIN voit tout
  const visibleAgents = useMemo(() => {
    if (user.role === UserRole.MANAGER) {
      return users.filter(u => String(u.managerId) === String(user.id) || String(u.id) === String(user.id));
    }
    return users;
  }, [users, user.id, user.role]);

  const sectorOptions = useMemo(() => {
    return Array.from(
      new Set(
        leads
          .map(l => String(l.sectorActivity || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [leads]);

  // Filtrage global (Déplacé avant le return pour éviter l'erreur hooks order)
  const filteredLeads = useMemo(() => {
    // Filtre période — uniquement dans la vue Dashboard (les vues Leads/
    // Kanban/Cartes gardent leur pleine liste). 0 = tout l'historique.
    const applyPeriod = activeView === 'dashboard' && dashboardPeriodDays > 0;
    const cutoff = applyPeriod ? Date.now() - dashboardPeriodDays * 86400000 : 0;
    return leads.filter(l => {
        const matchesAgent = filterAgent === 'all' || String(l.assignedTo) === String(filterAgent);
        const matchesStatus = filterStatus === 'all' || l.status === filterStatus;
        const matchesSector = filterSector === 'all' || String(l.sectorActivity || '') === String(filterSector);
        const matchesSearch = (l.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (l.email || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTag = !searchTag.trim() || (l.tag || '').toLowerCase().includes(searchTag.toLowerCase());
        const matchesColor = filterColor === 'all' || (effectiveRevenueColors[l.potentialRevenue || ''] === filterColor);
        const matchesPeriod = !applyPeriod || (l.createdAt && new Date(l.createdAt).getTime() >= cutoff);
        return matchesAgent && matchesStatus && matchesSector && matchesSearch && matchesTag && matchesColor && matchesPeriod;
    });
  }, [leads, filterAgent, filterStatus, filterSector, filterColor, searchTerm, searchTag, effectiveRevenueColors, activeView, dashboardPeriodDays]);

  // Logique de tri (Déplacé avant le return)
  const sortedLeads = useMemo(() => {
    if (!sortConfig) return filteredLeads;
    
    return [...filteredLeads].sort((a, b) => {
        let valA: any = a[sortConfig.key as keyof Lead] || '';
        let valB: any = b[sortConfig.key as keyof Lead] || '';

        // Tri spécifique pour le revenu (conversion numérique)
        if (sortConfig.key === 'revenue' || sortConfig.key === 'potentialRevenue') {
            valA = parseInt((a.potentialRevenue || '0').replace(/\D/g, '')) || 0;
            valB = parseInt((b.potentialRevenue || '0').replace(/\D/g, '')) || 0;
        } 
        // Tri spécifique pour les dates
        else if (sortConfig.key === 'createdAt') {
            valA = new Date(valA).getTime();
            valB = new Date(valB).getTime();
        }
        else {
             valA = String(valA).toLowerCase();
             valB = String(valB).toLowerCase();
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });
  }, [filteredLeads, sortConfig]);

  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const paginatedLeads = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedLeads.slice(start, start + itemsPerPage);
  }, [sortedLeads, currentPage, itemsPerPage]);

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === paginatedLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(paginatedLeads.map(l => l.id)));
    }
  };

  // Clear selection when page/filters change
  useEffect(() => { setSelectedLeadIds(new Set()); }, [currentPage, filterAgent, filterStatus, filterSector, filterColor, searchTerm, searchTag]);

  // Reset page quand les filtres changent
  useEffect(() => { setCurrentPage(1); }, [filterAgent, filterStatus, filterSector, filterColor, searchTerm, searchTag]);

  // ========== DASHBOARD CONFIGURABLE ==========
  // colSpan=2 = demi-largeur, colSpan=4 = pleine largeur (sera adapté au nb de colonnes réel)
  const isCompanyAdmin = user.role === UserRole.COMPANY_ADMIN;
  const DEFAULT_DASHBOARD_LAYOUT: DashboardWidgetDef[] = [
    { id: 'kpis',          label: 'Indicateurs clés',    icon: 'chart',    colSpan: 4, row: 0, col: 0, visible: true },
    { id: 'tasks',         label: 'Tâches à faire',       icon: 'task',     colSpan: 2, row: 1, col: 0, visible: true },
    { id: 'conversions',   label: 'Conversions',          icon: 'percent',  colSpan: 2, row: 1, col: 2, visible: true },
    { id: 'pipeline',      label: 'Pipeline',             icon: 'pipeline', colSpan: 4, row: 2, col: 0, visible: true },
    { id: 'recentLeads',   label: 'Leads récents',        icon: 'users',    colSpan: 2, row: 3, col: 0, visible: true },
    { id: 'topAgents',     label: 'Top agents',           icon: 'trophy',   colSpan: 2, row: 3, col: 2, visible: true },
    { id: 'leadFunnel',    label: 'Entonnoir leads',      icon: 'funnel',   colSpan: 2, row: 4, col: 0, visible: true },
    { id: 'leadTrend',     label: 'Évolution leads',      icon: 'trend',    colSpan: 2, row: 4, col: 2, visible: true },
    { id: 'revenueByAgent',label: 'CA potentiel / Agent', icon: 'bar',      colSpan: 2, row: 5, col: 0, visible: true },
    ...(isCompanyAdmin ? [
      { id: 'gdprOpposition', label: 'Demandes RGPD', icon: 'shield', colSpan: 2 as const, row: 5, col: 2, visible: true },
    ] : []),
  ];

  const [deviceVariant, setDeviceVariant] = useState<'desktop' | 'mobile'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop'
  );
  useEffect(() => {
    const h = () => setDeviceVariant(window.innerWidth < 768 ? 'mobile' : 'desktop');
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const mergeLayout = (stored: any): DashboardWidgetDef[] => {
    if (!Array.isArray(stored)) return DEFAULT_DASHBOARD_LAYOUT;
    return DEFAULT_DASHBOARD_LAYOUT.map(def => {
      const found = stored.find((s: any) => s.id === def.id);
      if (!found) return def;
      return {
        ...def,
        visible: found.visible ?? def.visible,
        colSpan: found.colSpan ?? def.colSpan,
        row: found.row ?? def.row,
        col: found.col ?? def.col,
      };
    });
  };

  const [dashboardLayout, setDashboardLayout] = useState<DashboardWidgetDef[]>(DEFAULT_DASHBOARD_LAYOUT);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  // Pour chaque variant (desktop/mobile), un layout par nombre de colonnes.
  // Permet de conserver des configs distinctes selon la taille d'écran.
  const serverLayoutsRef = useRef<{ desktop: ByColsLayouts; mobile: ByColsLayouts }>({ desktop: {}, mobile: {} });
  const layoutSaveTimerRef = useRef<number | null>(null);
  const [showDashboardConfig, setShowDashboardConfig] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState<boolean>(false);
  const [oppositionPendingCount, setOppositionPendingCount] = useState(0);

  useEffect(() => {
    if (!isCompanyAdmin) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await fetch('api/index.php?action=opposition_pending_count', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setOppositionPendingCount(Number(data?.count || 0));
      } catch {}
    };
    fetchCount();
    return () => { cancelled = true; };
  }, [isCompanyAdmin, user.id]);

  // Chargement initial des préférences depuis la BDD
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const prefs = await api.getUserPrefs();
      if (cancelled) return;
      if (prefs) {
        serverLayoutsRef.current = {
          desktop: normalizeStoredLayout(prefs.dashboardLayoutDesktop),
          mobile:  normalizeStoredLayout(prefs.dashboardLayoutMobile),
        };
        setOnboardingDismissed(prefs.onboardingDismissed);
        // Le useEffect ci-dessous (réagit à dashboardColumnCount) chargera
        // le layout adapté une fois prefsLoaded passé à true.
      }
      setPrefsLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const dismissOnboarding = () => {
    setOnboardingDismissed(true);
    api.dismissOnboarding().catch(() => {});
    window.dispatchEvent(new CustomEvent('orion-onboarding-dismissed'));
  };
  const [pipelineModalCol, setPipelineModalCol] = useState<string | null>(null);
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const dashboardGridRef = useRef<HTMLDivElement>(null);
  const [dashboardGridWidth, setDashboardGridWidth] = useState(0);

  useEffect(() => {
    if (activeView !== 'dashboard') return;

    const node = dashboardGridRef.current;
    if (!node) return;

    const updateWidth = () => {
      const width = dashboardGridRef.current?.getBoundingClientRect().width ?? 0;
      setDashboardGridWidth(Math.round(width));
    };

    updateWidth();

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => updateWidth())
      : null;

    observer?.observe(node);
    window.addEventListener('resize', updateWidth);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [activeView]);

  // Largeur d'écran (viewport) — les seuils sont calibrés par rapport à la largeur d'écran
  // standard (2560 QHD → 5 cols, 3840 4K → 6 cols), pas la largeur du conteneur.
  const [viewportWidth, setViewportWidth] = useState<number>(() => typeof window !== 'undefined' ? window.innerWidth : 1920);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const dashboardColumnCount = useMemo(
    () => {
      // Fallback sur la largeur du conteneur si < seuils étroits (mobile/tablette) pour préserver la détection compacte
      const screenCols = getAdaptiveDashboardColumnCount(viewportWidth);
      if (dashboardGridWidth > 0 && dashboardGridWidth < 1500) {
        return getAdaptiveDashboardColumnCount(dashboardGridWidth);
      }
      return screenCols;
    },
    [viewportWidth, dashboardGridWidth]
  );
  const usesCompactDashboardLayout = dashboardColumnCount < 4;

  // Recharge le layout adapté quand on change de variant ou de nombre de colonnes
  // (passage entre écrans de tailles différentes). Si aucun layout n'est sauvegardé
  // pour ce nombre de colonnes, on auto-arrange depuis le plus proche existant
  // pour éviter les "trous" tout en respectant l'ordre de lecture.
  useEffect(() => {
    if (!prefsLoaded) return;
    const byCols = serverLayoutsRef.current[deviceVariant];
    const stored = pickLayoutForCols(byCols, dashboardColumnCount, DEFAULT_DASHBOARD_LAYOUT);
    setDashboardLayout(mergeLayout(stored));
  }, [deviceVariant, dashboardColumnCount, prefsLoaded]);

  // ---- Grille à positions fixes — résolution des chevauchements + zones de drop ----
  const { visibleWidgets, emptyCells, rowMap, numDisplayRows } = useMemo(() => {
    if (usesCompactDashboardLayout) {
      // Mode adaptatif (< 4 colonnes) : flux simple sans positionnement explicite
      // kpis exclu : rendu hors grille
      const visible = dashboardLayout.filter(w => w.visible && w.id !== 'kpis')
        .sort((a, b) => a.row - b.row || a.col - b.col);
      return {
        visibleWidgets: visible,
        emptyCells: [] as Array<{ row: number; col: number }>,
        rowMap: new Map<number, number>(),
        numDisplayRows: 0
      };
    }

    // Mode desktop : résolution des chevauchements avant tout calcul
    // kpis est rendu hors grille (toujours pleine largeur en haut) → on l'exclut
    const resolved = resolveOverlaps(
      dashboardLayout.filter(w => w.visible && w.id !== 'kpis'),
      dashboardColumnCount
    );

    if (!draggedWidgetId) {
      // Pas de drag : normaliser les indices de lignes (supprime les lignes vides)
      const usedRows = [...new Set(resolved.map(w => w.row))].sort((a, b) => a - b);
      const rMap = new Map(usedRows.map((r, i) => [r, i + 1])); // 1-indexed CSS grid
      return {
        visibleWidgets: resolved,
        emptyCells: [] as Array<{ row: number; col: number }>,
        rowMap: rMap,
        numDisplayRows: usedRows.length
      };
    }

    // Pendant le drag : positions réelles + zones de drop une ligne en dessous du dernier widget
    const maxRow = resolved.reduce((m, w) => Math.max(m, w.row), 0);
    const totalRows = maxRow + 2;
    const occupied = new Set<string>();
    resolved.forEach(w => {
      for (let dc = 0; dc < w.colSpan; dc++) occupied.add(`${w.row}-${w.col + dc}`);
    });
    const empty: Array<{ row: number; col: number }> = [];
    for (let r = 0; r < totalRows; r++) {
      for (let c = 0; c < dashboardColumnCount; c++) {
        if (!occupied.has(`${r}-${c}`)) empty.push({ row: r, col: c });
      }
    }
    const rMap = new Map(resolved.map(w => [w.row, w.row + 1]));
    return { visibleWidgets: resolved, emptyCells: empty, rowMap: rMap, numDisplayRows: totalRows };
  }, [dashboardLayout, draggedWidgetId, dashboardColumnCount, usesCompactDashboardLayout]);

  const topAgents = useMemo(() => {
    const agentWins: Record<string, number> = {};
    const wonIds = statuses.filter(s => s.isWon).map(s => s.id);
    filteredLeads.filter(l => wonIds.includes(l.status)).forEach(l => {
      agentWins[l.assignedTo] = (agentWins[l.assignedTo] || 0) + 1;
    });
    return Object.entries(agentWins)
      .map(([id, count]) => ({ id, name: users.find(u => String(u.id) === String(id))?.name || '...', count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filteredLeads, users]);

  // Tri vue cards
  const [cardSort, setCardSort] = useState<'name' | 'date' | 'revenue'>('date');

  // Données entonnoir (pyramide) — répartition des leads par statut
  const funnelData = useMemo(() => {
    const stages = statuses.map(s => ({
      name: s.label, status: s.id, color: s.color, gradient: `linear-gradient(135deg, ${s.color}, ${s.color}88)`
    }));
    const total = filteredLeads.length || 1;
    return stages.map(s => {
      const count = filteredLeads.filter(l => l.status === s.status).length;
      return { name: s.name, value: count, pct: Math.round((count / total) * 100), color: s.color, gradient: s.gradient };
    });
  }, [filteredLeads]);

  // Données courbe — progression des leads par mois et par statut
  const leadTrendData = useMemo(() => {
    const monthMap: Record<string, Record<string, number>> = {};
    filteredLeads.forEach(l => {
      const d = new Date(l.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) {
        const init: Record<string, number> = {};
        statuses.forEach(s => { init[s.id] = 0; });
        monthMap[key] = init;
      }
      monthMap[key][l.status] = (monthMap[key][l.status] || 0) + 1;
    });
    return Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, counts]) => {
        const row: Record<string, any> = {
          month: new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
        };
        statuses.forEach(s => { row[s.label] = counts[s.id] || 0; });
        return row;
      });
  }, [filteredLeads, statuses]);

  // Données histogramme — CA potentiel par agent
  const revenueByAgentData = useMemo(() => {
    const agentRevenue: Record<string, number> = {};
    filteredLeads.forEach(l => {
      const raw = (l.potentialRevenue || '').replace(/[^\d]/g, '');
      const val = parseInt(raw) || 0;
      if (val > 0) {
        agentRevenue[l.assignedTo] = (agentRevenue[l.assignedTo] || 0) + val;
      }
    });
    return Object.entries(agentRevenue)
      .map(([id, revenue]) => ({
        name: users.find(u => String(u.id) === String(id))?.name?.split(' ')[0] || 'N/A',
        revenue,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [filteredLeads, users]);

  // Fallback partagé pour les vues lazy.
  const lazyFallback = (
    <div className="flex-1 flex items-center justify-center text-slate-400">
      <div className="flex items-center gap-3">
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
        <span className="text-sm font-medium">Chargement…</span>
      </div>
    </div>
  );

  // Return conditionnel APRÈS tous les hooks
  if (activeView === 'planning') return <Suspense fallback={lazyFallback}><CalendarView currentUser={user} leads={leads} companyUsers={users} timezone={company.timezone} /></Suspense>;
  if (activeView === 'tasks') return <TasksView user={user} company={company} />;
  if (activeView === 'opportunities') return <Suspense fallback={lazyFallback}><OpportunitiesView user={user} company={company} /></Suspense>;

  const statsBreakdown: Record<string, number> = {};
  statuses.forEach(s => { statsBreakdown[s.id] = filteredLeads.filter(l => l.status === s.id).length; });

  const saveDashboardLayout = (layout: DashboardWidgetDef[]) => {
    setDashboardLayout(layout);
    const serialized = layout.map(w => ({ id: w.id, visible: w.visible, colSpan: w.colSpan, row: w.row, col: w.col }));
    // On stocke ce layout dans le bucket correspondant au nombre de colonnes
    // courant, pour que chaque taille d'écran conserve sa propre disposition.
    const map = serverLayoutsRef.current[deviceVariant];
    map[dashboardColumnCount] = serialized;
    if (layoutSaveTimerRef.current) clearTimeout(layoutSaveTimerRef.current);
    layoutSaveTimerRef.current = window.setTimeout(() => {
      api.saveDashboardLayout(deviceVariant, { byCols: map as unknown as Record<string, any[]> }).catch(() => {});
    }, 500);
  };

  const setWidgetColSpan = (widgetId: string, span: 1 | 2 | 3 | 4 | 5 | 6) => {
    // La résolution des chevauchements sera faite dans le useMemo ; on se contente de stocker le span
    const updated = dashboardLayout.map(w => {
      if (w.id !== widgetId) return w;
      const newCol = Math.min(w.col, Math.max(0, dashboardColumnCount - span));
      return { ...w, colSpan: span, col: newCol };
    });
    saveDashboardLayout(updated);
  };

  const toggleWidgetVisibility = (widgetId: string) => {
    saveDashboardLayout(dashboardLayout.map(w => w.id === widgetId ? { ...w, visible: !w.visible } : w));
  };


  // ---- Drag & Drop (positions fixes — pas de reflow) ----
  const handleWidgetDragStart = (e: React.DragEvent, widgetId: string) => {
    e.dataTransfer.setData('widget-id', widgetId);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => setDraggedWidgetId(widgetId), 0);
  };

  const handleWidgetDragEnd = () => { setDraggedWidgetId(null); setDropTarget(null); };

  const handleCellDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(key);
  };

  // Drop sur une cellule vide → déplace le widget à cette position
  const handleCellDrop = (e: React.DragEvent, targetRow: number, targetCol: number) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('widget-id');
    setDropTarget(null); setDraggedWidgetId(null);
    if (!sourceId) return;
    const src = dashboardLayout.find(w => w.id === sourceId);
    if (!src) return;
    const effectiveSpan = Math.min(src.colSpan, dashboardColumnCount) as DashboardWidgetDef['colSpan'];
    const newCol = Math.min(targetCol, Math.max(0, dashboardColumnCount - effectiveSpan));
    const newLayout = dashboardLayout.map(w =>
      w.id === sourceId ? { ...w, row: targetRow, col: newCol, colSpan: effectiveSpan } : w
    );
    saveDashboardLayout(newLayout);
  };

  // Drop sur un widget existant → échange les positions résolues (sans chevauchement)
  const handleWidgetDropOn = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('widget-id');
    setDropTarget(null); setDraggedWidgetId(null);
    if (!sourceId || sourceId === targetId) return;
    // Utiliser les positions résolues (visibleWidgets) comme source de vérité pour l'échange
    const srcResolved = visibleWidgets.find(w => w.id === sourceId);
    const tgtResolved = visibleWidgets.find(w => w.id === targetId);
    if (!srcResolved || !tgtResolved) return;
    saveDashboardLayout(dashboardLayout.map(w => {
      if (w.id === sourceId) return { ...w, row: tgtResolved.row, col: tgtResolved.col };
      if (w.id === targetId) return { ...w, row: srcResolved.row, col: srcResolved.col };
      return w;
    }));
  };

  // Stats pour widgets
  const totalLeads = filteredLeads.length;
  const wonStatuses = statuses.filter(s => s.isWon).map(s => s.id);
  const lostStatuses = statuses.filter(s => s.isLost).map(s => s.id);
  const wonCount = filteredLeads.filter(l => wonStatuses.includes(l.status)).length;
  const lostCount = filteredLeads.filter(l => lostStatuses.includes(l.status)).length;
  const conversionRate = (wonCount + lostCount) > 0
    ? Math.round((wonCount / (wonCount + lostCount)) * 100) : 0;
  const statusDistribution = statuses.map(s => ({
    id: s.id, label: s.label, count: statsBreakdown[s.id] || 0, color: s.color
  }));

  const recentLeads = [...filteredLeads].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);

  // Définition des colonnes du Tunnel
  const columns = [
    { id: 'fav', label: 'Favoris ★', color: 'bg-amber-50 text-amber-700 border-amber-100', dot: 'bg-amber-500', hexColor: '#f59e0b' },
    ...statuses.map(s => ({ id: s.id, label: s.label, color: s.badgeClass, dot: s.dotClass, hexColor: s.color })),
  ];

  const getLeadsForColumn = (colId: string) => {
      const base = colId === 'fav' ? filteredLeads.filter(l => l.isFavorite) : filteredLeads.filter(l => !l.isFavorite && l.status === colId);
      return [...base].sort((a, b) => {
        if (cardSort === 'name') return a.name.localeCompare(b.name);
        if (cardSort === 'revenue') return (parseFloat(b.potentialRevenue || '0') || 0) - (parseFloat(a.potentialRevenue || '0') || 0);
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  };

  // Colonnes Kanban (sans favoris — le statut prime)
  const kanbanColumns = statuses.map(s => ({ id: s.id, label: s.label, dot: s.dotClass, color: s.color }));

  const getKanbanLeads = (colId: string) => {
    return filteredLeads
      .filter(l => l.status === colId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    e.dataTransfer.setData('text/plain', leadId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedLeadId(leadId);
  };

  const handleDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(colId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('text/plain');
    setDragOverColumn(null);
    setDraggedLeadId(null);

    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.status === newStatus) return;

    // Mise à jour optimiste
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus as Lead['status'] } : l));

    try {
      await api.updateLead(leadId, { status: newStatus as Lead['status'] });
    } catch {
      // Rollback
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: lead.status } : l));
    }
  };

  const handleDragEnd = () => {
    setDraggedLeadId(null);
    setDragOverColumn(null);
  };

  const SortIcon = ({ colKey }: { colKey: keyof Lead | 'revenue' }) => {
    if (sortConfig?.key !== colKey) return <svg className="w-3 h-3 opacity-30 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
    return sortConfig.direction === 'asc' 
        ? <svg className="w-3 h-3 text-indigo-600 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" /></svg>
        : <svg className="w-3 h-3 text-indigo-600 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>;
  };

  return (
    <div className="orion-dashboard animate-in fade-in duration-500 flex-1 min-h-0 flex flex-col gap-4">
      {/* Top Stats — affiché uniquement hors dashboard (leads, etc.) */}
      {activeView !== 'dashboard' && leadsViewMode === 'list' && (
        <div className={`grid grid-cols-2 gap-3 flex-shrink-0`} style={{ gridTemplateColumns: `repeat(${Math.min(statuses.length, 6)}, minmax(0, 1fr))` }}>
          {statuses.map((s) => (
            <div key={s.id} className="orion-kpi orion-kpi-glossy p-4 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between">
              <div>
                   <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{s.label}</div>
                   <div className="text-2xl font-black" style={{ color: s.color }}>{statsBreakdown[s.id] || 0}</div>
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center opacity-50" style={{ backgroundColor: s.color + '20' }}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }}></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barre d'actions Commune */}
      <div className={`orion-toolbar relative flex flex-col gap-2 p-1.5 rounded-xl border border-slate-200 shadow-sm flex-shrink-0 ${showFilters ? 'z-30' : ''}`}>

        {/* ── Ligne principale : toujours une seule ligne gauche / droite ── */}
        <div className="flex items-center gap-2 px-1">

          {/* GAUCHE : search + bouton filtres */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {canSearch && (
              <div className="relative flex-1 min-w-0 max-w-xs">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 border-none rounded-xl pl-9 pr-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 placeholder:font-medium"
                />
              </div>
            )}
            {/* Bouton filtres — toujours visible, ouvre la modale de filtres */}
            {canFilters && (
              <button
                type="button"
                onClick={() => setShowFilters(true)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black transition-all flex-shrink-0 ${filterAgent !== 'all' || filterStatus !== 'all' || filterSector !== 'all' || filterColor !== 'all' ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6] border border-[rgba(17,197,214,0.3)]' : 'bg-[rgba(100,140,210,0.08)] text-[#546a96] border border-[rgba(100,140,210,0.15)]'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" /></svg>
                Filtres
                {(filterAgent !== 'all' || filterStatus !== 'all' || filterSector !== 'all' || filterColor !== 'all') && (
                  <span className="w-4 h-4 rounded-full bg-[#11c5d6] text-[9px] font-black text-white flex items-center justify-center">
                    {[filterAgent, filterStatus, filterSector, filterColor].filter(f => f !== 'all').length}
                  </span>
                )}
              </button>
            )}
            {canFilters && (filterAgent !== 'all' || filterStatus !== 'all' || filterSector !== 'all' || filterColor !== 'all') && (
              <button
                type="button"
                onClick={() => { setFilterAgent('all'); setFilterStatus('all'); setFilterSector('all'); setFilterColor('all'); setSearchTag(''); }}
                title="Effacer les filtres"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black text-rose-500 bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 transition-all flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                Effacer
              </button>
            )}
          </div>

          {/* DROITE : vue + ajouter + import + export */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {activeView === 'leads' && canViewSwitch && (
              <div className="flex items-center bg-[rgba(100,140,210,0.1)] rounded-xl p-1 gap-0.5">
                <button type="button" onClick={() => { setLeadsViewMode('list'); localStorage.setItem('orion_view_mode', 'list'); }} className={`p-1.5 rounded-lg transition-all ${leadsViewMode === 'list' ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96]'}`} title="Vue liste">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                </button>
                <button type="button" onClick={() => { setLeadsViewMode('cards'); localStorage.setItem('orion_view_mode', 'cards'); }} className={`p-1.5 rounded-lg transition-all ${leadsViewMode === 'cards' ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96]'}`} title="Vue cards">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
                </button>
                <button type="button" onClick={() => { setLeadsViewMode('kanban'); localStorage.setItem('orion_view_mode', 'kanban'); }} className={`p-1.5 rounded-lg transition-all ${leadsViewMode === 'kanban' ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96]'}`} title="Vue kanban">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>
                </button>
              </div>
            )}
            {canCreateLead && (
            <button type="button" onClick={() => setShowAddLead(true)} className="orion-primary-btn text-white px-3 sm:px-5 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all shadow-lg">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
              <span className="hidden sm:inline">AJOUTER</span>
            </button>
            )}
            {canImportCsv && (
              <>
                <input ref={csvInputRef} type="file" accept=".csv,.txt,.xlsx,.xls" onChange={handleImportFileSelected} className="hidden" />
                <button type="button" onClick={() => csvInputRef.current?.click()} disabled={isImporting} className="orion-icon-btn text-slate-600 px-2.5 py-2 rounded-xl transition-all" title="Importer CSV / Excel">
                  {isImporting ? <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m4-8l-4-4m0 0l-4 4m4-4v12" /></svg>}
                </button>
              </>
            )}
            {canExportCsv && (
              <div className="relative">
                <button ref={exportBtnRef} type="button" onClick={() => { if (exportBtnRef.current) setExportMenuRect(exportBtnRef.current.getBoundingClientRect()); setShowExportMenu(!showExportMenu); }} className="orion-icon-btn text-slate-600 px-2.5 py-2 rounded-xl transition-all" title="Exporter CSV">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                </button>
                {showExportMenu && exportMenuRect && createPortal(
                  <div ref={exportPanelRef} className="orion-popover w-72 rounded-2xl shadow-xl p-4 animate-in slide-in-from-top-2" style={{ position: 'fixed', top: `${exportMenuRect.bottom + 8}px`, right: `${window.innerWidth - exportMenuRect.right}px`, zIndex: 9999 }}>
                    <div className="space-y-3 mb-4">
                      <SelectField value={exportAgentId} onChange={setExportAgentId} options={[{ value: 'all', label: 'Tous les agents' }, ...visibleAgents.map(u => ({ value: u.id, label: u.name }))]} className="orion-export-select w-full rounded-xl px-3 py-2.5 text-xs font-bold" />
                      <div className="flex gap-2">
                        <input type="date" value={exportDateFrom} onChange={e => setExportDateFrom(e.target.value)} className="w-full orion-lead-select rounded-lg px-2 py-2 text-[10px] font-bold" />
                        <input type="date" value={exportDateTo} onChange={e => setExportDateTo(e.target.value)} className="w-full orion-lead-select rounded-lg px-2 py-2 text-[10px] font-bold" />
                      </div>
                    </div>
                    <button type="button" onClick={handleExport} disabled={isExporting} className="w-full orion-primary-btn text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">{isExporting ? '...' : 'Télécharger CSV'}</button>
                  </div>,
                  document.body
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Modale de filtres ── */}
        {showFilters && createPortal(
          <div
            className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFilters(false); }}
          >
            <div className="orion-modal-panel w-full max-w-lg rounded-3xl p-8 shadow-2xl border animate-in zoom-in-95">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-slate-800 dark:text-white">Filtres</h3>
                <button
                  type="button"
                  onClick={() => { setFilterAgent('all'); setFilterStatus('all'); setFilterSector('all'); setFilterColor('all'); setSearchTag(''); }}
                  className="text-[10px] font-black text-rose-500 hover:text-rose-700 uppercase tracking-widest"
                >
                  Tout effacer
                </button>
              </div>
              <div className="space-y-4">
                {canTagSearch && (
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Tag</label>
                    <input type="text" placeholder="Rechercher par tag..." value={searchTag} onChange={(e) => setSearchTag(e.target.value)} className="orion-modal-input w-full rounded-xl px-4 py-3 font-bold text-sm outline-none" />
                  </div>
                )}
                {canFilters && (
                  <>
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Agent</label>
                      <SelectField value={filterAgent} onChange={setFilterAgent} options={[{ value: 'all', label: 'Tous les agents' }, ...visibleAgents.map(u => ({ value: u.id, label: u.name }))]} className="orion-modal-input w-full rounded-xl px-4 py-3 text-xs font-bold" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Statut</label>
                        <SelectField value={filterStatus} onChange={setFilterStatus} options={[{ value: 'all', label: 'Tous les statuts' }, ...statuses.map(s => ({ value: s.id, label: s.label }))]} className="orion-modal-input w-full rounded-xl px-4 py-3 text-xs font-bold" />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Secteur</label>
                        <SelectField value={filterSector} onChange={setFilterSector} options={[{ value: 'all', label: 'Tous les secteurs' }, ...sectorOptions.map(s => ({ value: s, label: s }))]} className="orion-modal-input w-full rounded-xl px-4 py-3 text-xs font-bold" />
                      </div>
                    </div>
                  </>
                )}
                {canColorFilter && (() => {
                  const colorMap: Record<string, string[]> = {};
                  Object.entries(effectiveRevenueColors).forEach(([bracket, hex]) => {
                    if (!hex) return;
                    if (!colorMap[hex]) colorMap[hex] = [];
                    colorMap[hex].push(bracket);
                  });
                  const uniqueColors = Object.entries(colorMap);
                  if (uniqueColors.length === 0) return null;
                  return (
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Potentiel</label>
                      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.15)]">
                        <div className="flex items-center gap-2 flex-1">
                          {uniqueColors.map(([hex, brackets]) => (
                            <button
                              key={hex}
                              type="button"
                              title={brackets.join(', ')}
                              onClick={() => setFilterColor(filterColor === hex ? 'all' : hex)}
                              className="flex-shrink-0 transition-all duration-150 focus:outline-none"
                              style={{
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                backgroundColor: hex,
                                transform: filterColor === hex ? 'scale(1.35)' : 'scale(1)',
                                boxShadow: filterColor === hex ? `0 0 0 2px rgba(255,255,255,0.15), 0 0 0 3.5px ${hex}` : 'none',
                                opacity: filterColor !== 'all' && filterColor !== hex ? 0.3 : 1,
                              }}
                            />
                          ))}
                        </div>
                        {filterColor !== 'all' && (
                          <button
                            type="button"
                            title="Effacer le filtre couleur"
                            onClick={() => setFilterColor('all')}
                            className="text-[11px] font-black text-[#546a96] hover:text-rose-400 transition-colors"
                          >✕</button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="flex gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="orion-modal-primary-btn flex-1 text-white rounded-xl py-3 font-bold text-xs shadow-lg"
                >
                  OK
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* CONTENU CONDITIONNEL : DASHBOARD CONFIGURABLE vs LISTE */}
      {activeView === 'dashboard' ? (
        // VUE DASHBOARD CONFIGURABLE — Grille CSS 4 colonnes avec positionnement explicite
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Header dashboard + bouton config */}
          <div className="flex items-center justify-between mb-4 px-1 flex-shrink-0 gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <svg className="w-4 h-4 text-[#11c5d6]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>
              <span className="text-[10px] font-black uppercase tracking-widest text-[#546a96]">
                {usesCompactDashboardLayout ? 'Tableau de bord — mode adaptatif' : 'Tableau de bord — glissez pour réorganiser'}
              </span>
              {/* Sélecteur de période — filtre les leads par date de création */}
              <div className="flex items-center gap-1 ml-2 p-1 rounded-lg bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.1)]">
                {[
                  { d: 7,   label: '7j' },
                  { d: 30,  label: '30j' },
                  { d: 90,  label: '3 mois' },
                  { d: 365, label: '12 mois' },
                  { d: 0,   label: 'Tout' },
                ].map(p => (
                  <button
                    key={p.d}
                    type="button"
                    onClick={() => setDashboardPeriodDays(p.d)}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-colors ${
                      dashboardPeriodDays === p.d
                        ? 'bg-[#11c5d6] text-white shadow'
                        : 'text-[#7f93bc] hover:text-[#e0eaff] hover:bg-[rgba(17,197,214,0.1)]'
                    }`}
                    title={p.d === 0 ? 'Tout l\'historique' : `Leads créés dans les ${p.label}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {canDashboardCustomize && (
            <div className="flex items-center gap-2">
              {!usesCompactDashboardLayout && (
                <button
                  type="button"
                  onClick={() => {
                    // kpis est hors grille : on l'exclut du compactage puis on le remet tel quel
                    const kpisWidget = dashboardLayout.find(w => w.id === 'kpis');
                    const others = dashboardLayout.filter(w => w.id !== 'kpis');
                    const arranged = autoArrangeLayout(others, dashboardColumnCount);
                    saveDashboardLayout(kpisWidget ? [kpisWidget, ...arranged] : arranged);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all text-[#546a96] hover:text-[#11c5d6] hover:bg-[rgba(17,197,214,0.1)] border border-transparent hover:border-[rgba(17,197,214,0.2)]"
                  title="Réorganiser automatiquement tous les widgets"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Auto
                </button>
              )}
              <button type="button" onClick={() => setShowDashboardConfig(!showDashboardConfig)} className={`p-2 rounded-lg transition-all ${showDashboardConfig ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96] hover:text-[#7f93bc]'}`} title="Configurer les widgets">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </button>
            </div>
            )}
          </div>

          {/* Panneau config (toggle) */}
          {canDashboardCustomize && showDashboardConfig && (
            <div className="orion-widget-config-panel p-4 mb-4 mx-1 flex-shrink-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-3">Afficher / Masquer les widgets</div>
              <div className="flex flex-wrap gap-2">
                {dashboardLayout.map(w => (
                  <button key={w.id} type="button" onClick={() => toggleWidgetVisibility(w.id)} className={`text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all ${w.visible ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6] border border-[rgba(17,197,214,0.3)]' : 'bg-[rgba(100,140,210,0.08)] text-[#546a96] border border-transparent'}`}>
                    {w.visible ? '✓ ' : ''}{w.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conteneur flex colonne : KPI toujours en haut, grille en dessous */}
          <div className="flex flex-col gap-4 px-1 pb-4 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">

          {/* ── Widget KPI — toujours pleine largeur, hors grille ── */}
          {(() => {
            const widget = dashboardLayout.find(w => w.id === 'kpis' && w.visible);
            if (!widget) return null;
            return (
              <div
                key="kpis"
                className={`orion-widget orion-widget-compact group flex-shrink-0 ${draggedWidgetId === widget.id ? 'orion-widget-dragging' : ''}`}
              >
                <div className="orion-widget-header">
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-[#546a96] opacity-50" fill="currentColor" viewBox="0 0 20 20"><circle cx="6" cy="4" r="1.5"/><circle cx="14" cy="4" r="1.5"/><circle cx="6" cy="10" r="1.5"/><circle cx="14" cy="10" r="1.5"/><circle cx="6" cy="16" r="1.5"/><circle cx="14" cy="16" r="1.5"/></svg>
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#7f93bc]">{widget.label}</span>
                  </div>
                  {canDashboardCustomize && (
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                      <button type="button" onClick={(e) => { e.stopPropagation(); toggleWidgetVisibility(widget.id); }} className="p-1 rounded-md text-[#546a96] hover:text-rose-400 hover:bg-[rgba(244,63,94,0.1)] transition-all" title="Masquer">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${statuses.length}, minmax(90px, 1fr))` }}>
                    {statuses.map((s) => (
                      <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.12)] min-w-0">
                        <div className="min-w-0">
                          <div className="text-[9px] font-black text-[#546a96] uppercase tracking-widest mb-1 leading-tight truncate">{s.label}</div>
                          <div className="text-2xl font-black" style={{ color: s.color }}>{statsBreakdown[s.id] || 0}</div>
                        </div>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ backgroundColor: s.color + '18' }}>
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Carte de bienvenue (onboarding) ── */}
          {leads.length === 0 && !onboardingDismissed && (
            <div className="orion-widget orion-widget-compact flex-shrink-0 border border-[rgba(17,197,214,0.25)] bg-gradient-to-br from-[rgba(17,197,214,0.06)] to-[rgba(99,102,241,0.04)]">
              <div className="p-8">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#11c5d6] to-[#6366f1] flex items-center justify-center flex-shrink-0 shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-black text-[#e0eaff] mb-1">Bienvenue sur votre CRM, {user.name.split(' ')[0]} !</h3>
                      <p className="text-[13px] text-[#7f93bc] leading-relaxed">
                        Votre tableau de bord est prêt. Commencez par configurer votre CRM pour l'adapter à votre activité — cliquez sur l'icône <span className="inline-block align-middle mx-0.5"><svg className="w-4 h-4 inline text-[#11c5d6]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></span> <strong className="text-[#11c5d6]">Paramètres</strong> dans le menu à gauche (elle clignote !).
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={dismissOnboarding}
                    className="p-1.5 rounded-lg text-[#546a96] hover:text-[#e0eaff] hover:bg-white/5 transition-all flex-shrink-0"
                    title="Fermer le guide"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { step: '1', label: 'Configurer vos statuts', desc: 'Définissez les étapes de votre pipeline commercial', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 104 0M9 5a2 2 0 012 2h2a2 2 0 012-2' },
                    { step: '2', label: 'Définir vos champs', desc: 'Personnalisez les informations collectées sur vos prospects', icon: 'M4 6h16M4 12h16M4 18h7' },
                    { step: '3', label: 'Inviter votre équipe', desc: 'Ajoutez vos collaborateurs au CRM', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
                    { step: '4', label: 'Créer votre 1er prospect', desc: 'Commencez à remplir votre pipeline', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
                  ].map(item => (
                    <div key={item.step} className="p-4 rounded-xl bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.12)]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[11px] font-black flex items-center justify-center">{item.step}</div>
                        <svg className="w-4 h-4 text-[#7f93bc]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} /></svg>
                      </div>
                      <div className="text-[12px] font-black text-[#e0eaff] mb-1">{item.label}</div>
                      <div className="text-[10px] text-[#546a96] leading-relaxed">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Grille CSS — tous les widgets sauf KPI (masquée en état vide non-dismissé) */}
          {(leads.length > 0 || onboardingDismissed) && (
          <div
            ref={dashboardGridRef}
            className="orion-dashboard-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${dashboardColumnCount}, minmax(0, 1fr))`,
              gap: '1rem',
              alignItems: 'stretch',
              alignContent: 'start',
            }}
          >
            {/* Cellules vides = zones de drop */}
            {canDashboardCustomize && emptyCells.map(cell => {
              const key = `${cell.row}-${cell.col}`;
              return (
                <div
                  key={`empty-${key}`}
                  style={{ gridRow: cell.row + 1, gridColumn: `${cell.col + 1} / span 1` }}
                  onDragOver={(e) => handleCellDragOver(e, key)}
                  onDrop={(e) => handleCellDrop(e, cell.row, cell.col)}
                  onDragLeave={() => setDropTarget(null)}
                  className={`rounded-xl border-2 border-dashed transition-all min-h-[60px] flex items-center justify-center ${
                    dropTarget === key
                      ? 'border-[rgba(17,197,214,0.6)] bg-[rgba(17,197,214,0.08)]'
                      : 'border-[rgba(100,140,210,0.15)] bg-[rgba(100,140,210,0.03)]'
                  }`}
                >
                  <span className="text-[9px] font-bold text-[#3d5280]">Déposer ici</span>
                </div>
              );
            })}

            {/* Widgets (hors KPI) */}
            {visibleWidgets.filter(w => w.id !== 'kpis').map(widget => {
              const effectiveSpan = Math.min(widget.colSpan, dashboardColumnCount);
              const effectiveCol = Math.min(widget.col, Math.max(0, dashboardColumnCount - effectiveSpan));
              const widgetPlacement = usesCompactDashboardLayout
                ? { gridColumn: `span ${effectiveSpan}` }
                : {
                    gridRow: rowMap.get(widget.row) ?? widget.row + 1,
                    gridColumn: `${effectiveCol + 1} / span ${effectiveSpan}`
                  };

              return (
              <div
                key={widget.id}
                style={widgetPlacement}
                draggable={canDashboardCustomize && !usesCompactDashboardLayout}
                onDragStart={canDashboardCustomize && !usesCompactDashboardLayout ? (e) => handleWidgetDragStart(e, widget.id) : undefined}
                onDragEnd={canDashboardCustomize && !usesCompactDashboardLayout ? handleWidgetDragEnd : undefined}
                onDragOver={canDashboardCustomize && !usesCompactDashboardLayout ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDropTarget(`w-${widget.id}`); } : undefined}
                onDrop={canDashboardCustomize && !usesCompactDashboardLayout ? (e) => handleWidgetDropOn(e, widget.id) : undefined}
                onDragLeave={canDashboardCustomize && !usesCompactDashboardLayout ? () => setDropTarget(null) : undefined}
                className={`orion-widget group ${widget.id === 'kpis' ? 'orion-widget-compact' : ''} ${draggedWidgetId === widget.id ? 'orion-widget-dragging' : ''} ${dropTarget === `w-${widget.id}` && draggedWidgetId && draggedWidgetId !== widget.id ? 'orion-widget-drop-target' : ''}`}
              >
                {/* Widget header */}
                <div className="orion-widget-header">
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-[#546a96] opacity-50" fill="currentColor" viewBox="0 0 20 20"><circle cx="6" cy="4" r="1.5"/><circle cx="14" cy="4" r="1.5"/><circle cx="6" cy="10" r="1.5"/><circle cx="14" cy="10" r="1.5"/><circle cx="6" cy="16" r="1.5"/><circle cx="14" cy="16" r="1.5"/></svg>
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#7f93bc]">{widget.label}</span>
                  </div>
                  {canDashboardCustomize && (
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                    {([1, 2, 3, 4, 5, 6] as const).filter(s => s <= dashboardColumnCount).map(s => (
                      <button key={s} type="button" title={`${s} col`} onClick={(e) => { e.stopPropagation(); setWidgetColSpan(widget.id, s); }}
                        className={`w-5 h-4 rounded flex items-center justify-center text-[8px] font-black transition-all ${widget.colSpan === s ? 'bg-[rgba(17,197,214,0.25)] text-[#11c5d6]' : 'text-[#3d5280] hover:text-[#7f93bc] hover:bg-[rgba(100,140,210,0.1)]'}`}
                      >{s}</button>
                    ))}
                    {!usesCompactDashboardLayout && <div className="w-px h-3 bg-[rgba(100,140,210,0.2)] mx-0.5"></div>}
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleWidgetVisibility(widget.id); }} className="p-1 rounded-md text-[#546a96] hover:text-rose-400 hover:bg-[rgba(244,63,94,0.1)] transition-all" title="Masquer">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  )}
                </div>

                {/* Widget content */}
                <div className="p-5">
                  {widget.id === 'kpis' && (
                    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${statuses.length}, minmax(90px, 1fr))` }}>
                      {statuses.map((s) => (
                        <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.12)] min-w-0">
                          <div className="min-w-0">
                            <div className="text-[9px] font-black text-[#546a96] uppercase tracking-widest mb-1 leading-tight truncate">{s.label}</div>
                            <div className="text-2xl font-black" style={{ color: s.color }}>{statsBreakdown[s.id] || 0}</div>
                          </div>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ backgroundColor: s.color + '18' }}>
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {widget.id === 'tasks' && (() => {
                    const todo = tasks.filter(t => t.status === 'todo');
                    const overdue = todo.filter(t => new Date(t.dueAt).getTime() < Date.now()).length;
                    const dueSoon = todo.filter(t => { const r = new Date(t.dueAt).getTime() - Date.now(); return r >= 0 && r <= 2 * 60 * 60 * 1000; }).length;
                    const next = [...todo].sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()).slice(0, 4);
                    return (
                      <div>
                        <div className="flex items-center gap-4 mb-4">
                          <div className="text-3xl font-black text-[#e0eaff]">{todo.length}</div>
                          <div className="flex items-center gap-3 text-[11px]">
                            <span className="font-bold text-rose-400">{overdue} en retard</span>
                            <span className="font-bold text-amber-400">{dueSoon} proches</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {next.length === 0 ? <div className="text-xs text-[#546a96]">Aucune tâche ouverte.</div> : next.map(task => (
                            <div key={task.id} className="flex items-center justify-between p-2.5 rounded-lg bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.1)]">
                              <span className="text-xs font-bold text-[#c8daf5] truncate flex-1 mr-3">{task.title}</span>
                              <span className="text-[10px] font-bold text-[#546a96] flex-shrink-0">{new Date(task.dueAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {widget.id === 'gdprOpposition' && (
                    <div>
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`text-3xl font-black ${oppositionPendingCount > 0 ? 'text-rose-400' : 'text-[#e0eaff]'}`}>
                          {oppositionPendingCount}
                        </div>
                        <div className="text-[10px] font-bold text-[#546a96] leading-snug">
                          Demande{oppositionPendingCount > 1 ? 's' : ''} à traiter<br/>
                          <span className="text-[#7f93bc]">Délai légal 30 jours (art. 21 RGPD)</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          sessionStorage.setItem('orion_settings_tab', 'opposition');
                          window.dispatchEvent(new CustomEvent('orion-navigate-settings-opposition'));
                        }}
                        className={`w-full text-[11px] font-bold px-3 py-2.5 rounded-lg border transition-all ${
                          oppositionPendingCount > 0
                            ? 'bg-rose-500/10 border-rose-500/30 text-rose-300 hover:bg-rose-500/20'
                            : 'bg-[rgba(100,140,210,0.08)] border-[rgba(100,140,210,0.15)] text-[#7f93bc] hover:bg-[rgba(100,140,210,0.12)]'
                        }`}
                      >
                        {oppositionPendingCount > 0 ? 'Traiter les demandes →' : 'Voir l\'onglet Oppositions →'}
                      </button>
                    </div>
                  )}

                  {widget.id === 'conversions' && (
                    <div>
                      <div className="flex items-center gap-3 mb-5">
                        <div className="text-3xl font-black text-[#11c5d6]">{conversionRate}%</div>
                        <div className="text-[10px] font-bold text-[#546a96]">Taux de conversion<br/>(Gagnés / Décidés)</div>
                      </div>
                      <div className="space-y-2.5">
                        {statusDistribution.map(s => (
                          <div key={s.id}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] font-bold text-[#7f93bc]">{s.label}</span>
                              <span className="text-[10px] font-black text-[#c8daf5]">{s.count} <span className="text-[#546a96]">({totalLeads > 0 ? Math.round((s.count / totalLeads) * 100) : 0}%)</span></span>
                            </div>
                            <div className="h-2 rounded-full bg-[rgba(100,140,210,0.1)] overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${totalLeads > 0 ? (s.count / totalLeads) * 100 : 0}%`, backgroundColor: s.color }}></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {widget.id === 'pipeline' && (
                    <div className="flex gap-3 overflow-x-auto pb-2" style={{ minWidth: 0 }}>
                      {columns.filter(c => c.id !== 'fav').map(col => {
                        const colLeads = getLeadsForColumn(col.id);
                        const totalRevenue = colLeads.reduce((acc, l) => { const m = (l.potentialRevenue || '').match(/(\d[\d\s]*)/); return acc + (m ? parseInt(m[0].replace(/\s/g, '')) : 0); }, 0);
                        return (
                          <div key={col.id} onClick={() => setPipelineModalCol(col.id)} className="rounded-xl bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.12)] p-3 cursor-pointer hover:border-[rgba(17,197,214,0.35)] hover:bg-[rgba(17,197,214,0.04)] transition-all flex-shrink-0 min-w-[140px] flex-1">
                            <div className="flex items-center gap-2 mb-2"><div className={`w-2 h-2 rounded-full ${col.dot}`}></div><span className="text-[10px] font-black text-[#7f93bc] uppercase">{col.label}</span></div>
                            <div className="text-xl font-black text-[#e0eaff] mb-1">{colLeads.length}</div>
                            {totalRevenue > 0 && <div className="text-[10px] font-bold text-[#546a96]">~{totalRevenue.toLocaleString()} {currencySymbol}</div>}
                            <div className="mt-2 space-y-1.5">
                              {colLeads.slice(0, 3).map(lead => (
                                <div key={lead.id} onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); }} className="flex items-center gap-2 p-1.5 rounded-lg bg-[rgba(100,140,210,0.06)] hover:bg-[rgba(17,197,214,0.1)] cursor-pointer transition-colors">
                                  <div className={`w-5 h-5 rounded flex items-center justify-center text-[8px] font-black text-white ${col.dot}`}>{lead.name.charAt(0)}</div>
                                  <span className="text-[10px] font-bold text-[#c8daf5] truncate">{lead.name}</span>
                                </div>
                              ))}
                              {colLeads.length > 3 && <div className="text-[9px] font-bold text-[#11c5d6] text-center">Voir les {colLeads.length} leads →</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {widget.id === 'recentLeads' && (
                    <div className="space-y-2.5 overflow-y-auto max-h-[16rem]">
                      {recentLeads.length === 0 ? <div className="text-xs text-[#546a96]">Aucun lead.</div> : recentLeads.map(lead => (
                        <div key={lead.id} onClick={() => setSelectedLead(lead)} className="flex items-center gap-3 p-2.5 rounded-lg bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.1)] hover:border-[rgba(17,197,214,0.3)] cursor-pointer transition-all">
                          <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center text-[10px] font-black text-white flex-shrink-0">{lead.name.charAt(0)}</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-[#e0eaff] truncate">{lead.name}</div>
                            <div className="text-[10px] text-[#546a96] truncate">{lead.clientCompanyName || lead.sectorActivity || ''}</div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-[10px] font-black text-[#7f93bc]">{lead.potentialRevenue || '-'}</div>
                            <div className="text-[8px] font-bold text-[#3d5280]">{formatDateShort(lead.createdAt)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {widget.id === 'topAgents' && (
                    <div className="space-y-2.5">
                      {topAgents.length === 0 ? <div className="text-xs text-[#546a96]">Aucun lead gagné.</div> : topAgents.map((agent, ai) => (
                        <div key={agent.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.1)]">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black" style={{ backgroundColor: ai === 0 ? 'rgba(245,158,11,0.2)' : ai === 1 ? 'rgba(148,163,184,0.2)' : 'rgba(180,130,80,0.2)', color: ai === 0 ? '#f59e0b' : ai === 1 ? '#94a3b8' : '#b4824f' }}>{ai + 1}</div>
                          <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.id}`} className="w-7 h-7 rounded-full bg-[rgba(100,140,210,0.1)]" alt="" />
                          <div className="flex-1 min-w-0"><div className="text-xs font-bold text-[#e0eaff] truncate">{agent.name}</div></div>
                          <div className="flex items-center gap-1.5"><span className="text-sm font-black text-emerald-400">{agent.count}</span><span className="text-[9px] font-bold text-[#546a96]">gagnés</span></div>
                        </div>
                      ))}
                    </div>
                  )}

                  {widget.id === 'leadFunnel' && (
                    <div>
                      {funnelData.every(d => d.value === 0) ? (
                        <div className="text-xs text-[#546a96]">Aucun lead à afficher.</div>
                      ) : (() => {
                        const n = funnelData.length;
                        const h = 36;
                        // n+1 paliers pour que le dernier étage ait aussi un vrai trapèze
                        const widths = Array.from({ length: n + 1 }, (_, i) => 100 - (i * (100 - 14)) / n);
                        return (
                          <div className="flex flex-col items-center" style={{ gap: 4 }}>
                            {funnelData.map((stage, i) => {
                              const topW = widths[i];
                              const botW = widths[i + 1];
                              const tl = (100 - topW) / 2;
                              const tr = 100 - tl;
                              const bl = (100 - botW) / 2;
                              const br = 100 - bl;
                              return (
                                <div key={stage.name} className="relative w-full" style={{ height: h }}>
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      background: stage.gradient,
                                      clipPath: `polygon(${tl}% 0%, ${tr}% 0%, ${br}% 100%, ${bl}% 100%)`,
                                    }}
                                  >
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)', pointerEvents: 'none' }} />
                                  </div>
                                  <div className="absolute inset-0 flex items-center justify-center gap-3" style={{ height: h }}>
                                    <span className="text-[11px] font-bold text-white/80">{stage.name}</span>
                                    <span className="text-lg font-black text-white" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>{stage.value}</span>
                                    <span className="text-[10px] font-bold text-white/55">{stage.pct}%</span>
                                  </div>
                                </div>
                              );
                            })}
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-[9px] font-black uppercase tracking-widest text-[#546a96]">Total</span>
                              <span className="text-lg font-black text-[#e0eaff]">{funnelData.reduce((s, d) => s + d.value, 0)}</span>
                              <span className="text-[10px] font-bold text-[#546a96]">leads</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {widget.id === 'leadTrend' && (
                    <div>
                      {leadTrendData.length === 0 ? (
                        <div className="text-xs text-[#546a96]">Pas assez de données.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <LineChart data={leadTrendData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,140,210,0.12)" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#7f93bc', fontWeight: 700 }} axisLine={{ stroke: 'rgba(100,140,210,0.2)' }} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#7f93bc', fontWeight: 700 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <RechartsTooltip
                              contentStyle={{ backgroundColor: '#0e1c3a', border: '1px solid rgba(100,140,210,0.2)', borderRadius: 12, fontSize: 12, color: '#e0eaff' }}
                              itemStyle={{ color: '#e0eaff' }}
                            />
                            <Legend wrapperStyle={{ fontSize: 10, fontWeight: 700 }} />
                            {statuses.map(s => (
                              <Line key={s.id} type="monotone" dataKey={s.label} stroke={s.color} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  )}

                  {widget.id === 'revenueByAgent' && (
                    <div>
                      {loading || users.length === 0 ? (
                        <div className="text-xs text-[#546a96]">Chargement...</div>
                      ) : revenueByAgentData.length === 0 ? (
                        <div className="text-xs text-[#546a96]">Aucune donnée de CA.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={revenueByAgentData} margin={{ top: 5, right: 10, left: -5, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,140,210,0.12)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#7f93bc', fontWeight: 700 }} axisLine={{ stroke: 'rgba(100,140,210,0.2)' }} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#7f93bc', fontWeight: 700 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
                            <RechartsTooltip
                              contentStyle={{ backgroundColor: '#0e1c3a', border: '1px solid rgba(100,140,210,0.2)', borderRadius: 12, fontSize: 12, color: '#e0eaff' }}
                              formatter={(value: number) => [`${value.toLocaleString('fr-FR')} ${currencySymbol}`, 'CA potentiel']}
                              cursor={{ fill: 'rgba(17,197,214,0.08)' }}
                            />
                            <Bar dataKey="revenue" name="CA potentiel" fill="url(#revenueGradient)" radius={[6, 6, 0, 0]} maxBarSize={50} />
                            <defs>
                              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#11c5d6" />
                                <stop offset="100%" stopColor="#6366f1" />
                              </linearGradient>
                            </defs>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )})}
          </div>
          )}{/* fin grille (conditionnelle en onboarding) */}
          </div>{/* fin wrapper flex KPI + grille */}
        </div>
      ) : leadsViewMode === 'cards' ? (
        // VUE CARDS (PROSPECTS) — groupée par statut, 20 vignettes par statut
        // avec boutons "Afficher plus" (+20) et "Afficher moins" (-20).
        <div className="flex-1 min-h-0 overflow-y-auto pb-4">
          <div className="flex items-center gap-2 mb-4 px-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#546a96]">Trier par</span>
            {(['date', 'name', 'revenue'] as const).map(k => (
              <button key={k} type="button" onClick={() => setCardSort(k)} className={`text-[10px] font-black px-3 py-1 rounded-lg transition-all ${cardSort === k ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96] hover:text-[#7f93bc] bg-[rgba(100,140,210,0.08)]'}`}>
                {k === 'date' ? 'Date' : k === 'name' ? 'Nom' : 'Montant'}
              </button>
            ))}
          </div>
          {columns.map(col => {
            const colLeads = getLeadsForColumn(col.id);
            if (colLeads.length === 0) return null;
            const shown = cardsShownByCol[col.id] ?? LEADS_PAGE_SIZE;
            const visibleLeads = colLeads.slice(0, shown);
            const remaining = Math.max(0, colLeads.length - shown);
            const canCollapse = shown > LEADS_PAGE_SIZE;
            return (
              <div key={col.id} className="mb-6">
                <div className="flex items-center gap-2.5 mb-3 px-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.hexColor }}></div>
                  <h3 className="text-xs font-black text-[#e0eaff] uppercase tracking-tight">{col.label}</h3>
                  <span className="bg-[rgba(100,140,210,0.15)] text-[#7f93bc] text-[10px] font-bold px-2 py-0.5 rounded-md">{colLeads.length}</span>
                  <span className="text-[10px] text-[#546a96] font-medium">· {visibleLeads.length} affiché{visibleLeads.length > 1 ? 's' : ''}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 px-1">
                  {visibleLeads.map(lead => {
                    const revColor = lead.potentialRevenue ? effectiveRevenueColors[lead.potentialRevenue] : undefined;
                    return (
                    <div
                      key={lead.id}
                      onClick={() => setSelectedLead(lead)}
                      className="orion-lead-card p-4 rounded-xl border border-[rgba(100,140,210,0.15)] hover:border-[rgba(17,197,214,0.3)] hover:scale-[1.02] transition-all cursor-pointer group/card shadow-sm relative"
                      style={{
                        ...(revColor ? { '--rev-border-color': revColor } : {}),
                        ...(cardBg ? { '--card-bg': cardBg } : {}),
                        ...(cardName ? { '--card-name': cardName } : {}),
                        ...(cardEmail ? { '--card-email': cardEmail } : {})
                      } as React.CSSProperties}
                    >
                      {cardConfig.showQuickCall && lead.phone && (
                        <a
                          href={`tel:${lead.phone.replace(/\s/g, '')}`}
                          onClick={e => e.stopPropagation()}
                          className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-all z-10 opacity-0 group-hover/card:opacity-100"
                          title={`Appeler ${lead.name}`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                        </a>
                      )}

                      <div className="flex justify-between items-start mb-2.5">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-black text-white shadow-sm flex-shrink-0" style={{ backgroundColor: col.hexColor }}>
                            {lead.name.charAt(0)}
                          </div>
                          <div className="text-sm font-bold transition-colors line-clamp-1 min-w-0" style={{ color: cardName || (isLight ? '#1e293b' : '#e0eaff') }}>{lead.name}</div>
                        </div>
                        {lead.isFavorite && col.id !== 'fav' && <span className="text-amber-400 text-lg leading-none flex-shrink-0">★</span>}
                      </div>

                      {(() => {
                        const cols = cardConfig.gridCols ?? 2;
                        const bodyFields = cardConfig.fields.filter(f =>
                          f.key !== 'name' && f.enabled && f.row !== undefined && f.col !== undefined
                        );
                        if (bodyFields.length === 0) return null;
                        return (
                          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridAutoRows: 'minmax(20px, auto)', gap: '3px 6px' }}>
                            {bodyFields.map(field => {
                              let content: React.ReactNode = null;
                              const fk = field.key;
                              if (fk === 'email') {
                                content = <div className="text-[10px] font-medium truncate" style={{ color: cardEmail || (isLight ? '#64748b' : '#546a96') }}>{displayEmail(lead.email, user.role, company.maskContacts)}</div>;
                              } else if (fk === 'phone') {
                                content = lead.phone ? <div className="text-[10px] font-medium truncate" style={{ color: cardEmail || (isLight ? '#64748b' : '#546a96') }}>{displayPhone(lead.phone, user.role, company.maskContacts)}</div> : null;
                              } else if (fk === 'clientCompanyName') {
                                content = lead.clientCompanyName ? <div className="text-[10px] font-bold truncate" style={{ color: cardEmail || (isLight ? '#64748b' : '#7f93bc') }}>🏢 {lead.clientCompanyName}</div> : null;
                              } else if (fk === 'sectorActivity') {
                                content = lead.sectorActivity ? <span className="text-[9px] font-bold bg-[rgba(100,140,210,0.12)] px-1.5 py-0.5 rounded-md" style={{ color: cardEmail || (isLight ? '#64748b' : '#7f93bc') }}>{lead.sectorActivity}</span> : null;
                              } else if (fk === 'status') {
                                const st = findStatus(statuses, lead.status);
                                content = st ? <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md border" style={statusBadgeStyle(st.color)}>{st.label}</span> : null;
                              } else if (fk === 'tag') {
                                content = lead.tag ? <span className="text-[9px] font-bold text-[#11c5d6] bg-[rgba(17,197,214,0.1)] px-1.5 py-0.5 rounded-md">{lead.tag}</span> : null;
                              } else if (fk === 'potentialRevenue') {
                                content = lead.potentialRevenue ? (
                                  <div className="flex items-center gap-1">
                                    {revColor && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: revColor }} />}
                                    <span className="text-[10px] font-black" style={{ color: revColor || (isLight ? '#1e293b' : '#e0eaff') }}>{lead.potentialRevenue}</span>
                                  </div>
                                ) : null;
                              } else if (fk === 'createdAt') {
                                content = <div className="text-[9px] font-bold text-[#3d5280] whitespace-nowrap">{formatDateShort(lead.createdAt)}</div>;
                              } else if (fk === 'agent') {
                                content = (
                                  <div className="flex items-center gap-1">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${lead.assignedTo}`} className="w-4 h-4 rounded-full bg-[rgba(100,140,210,0.1)] border border-[rgba(100,140,210,0.2)] flex-shrink-0" alt="" />
                                    <span className="text-[9px] font-bold truncate" style={{ color: cardName || (isLight ? '#1e293b' : '#e0eaff') }}>{users.find(u => String(u.id) === String(lead.assignedTo))?.name || '...'}</span>
                                  </div>
                                );
                              }
                              return (
                                <div key={fk} className="flex items-center min-h-[20px] min-w-0 overflow-hidden" style={{ gridRowStart: field.row! + 1, gridColumnStart: field.col! + 1, justifyContent: field.align === 'center' ? 'center' : field.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                  {content}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })}
                </div>
                {(remaining > 0 || canCollapse) && (
                  <div className="flex justify-center gap-2 mt-3 px-1">
                    {remaining > 0 && (
                      <button
                        type="button"
                        onClick={() => setCardsShownByCol(prev => ({ ...prev, [col.id]: shown + LEADS_PAGE_SIZE }))}
                        className="text-[11px] font-black uppercase tracking-widest text-[#11c5d6] hover:text-[#7be4ee] bg-[rgba(17,197,214,0.08)] hover:bg-[rgba(17,197,214,0.15)] px-4 py-2 rounded-lg transition-all"
                      >
                        + {Math.min(remaining, LEADS_PAGE_SIZE)} de plus ({remaining} restant{remaining > 1 ? 's' : ''})
                      </button>
                    )}
                    {canCollapse && (
                      <button
                        type="button"
                        onClick={() => setCardsShownByCol(prev => ({ ...prev, [col.id]: Math.max(LEADS_PAGE_SIZE, shown - LEADS_PAGE_SIZE) }))}
                        className="text-[11px] font-black uppercase tracking-widest text-[#7f93bc] hover:text-[#c8daf5] bg-[rgba(100,140,210,0.08)] hover:bg-[rgba(100,140,210,0.15)] px-4 py-2 rounded-lg transition-all"
                      >
                        Afficher moins
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : leadsViewMode === 'kanban' ? (
        // VUE KANBAN PIPELINE - DRAG & DROP
        <div className="flex-1 flex flex-col overflow-hidden pb-4">
          <div className="flex items-center gap-3 mb-4 px-1">
            <svg className="w-4 h-4 text-[#11c5d6]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#546a96]">Pipeline — Glissez les leads entre les colonnes</span>
          </div>
          <div className="orion-kanban-scroll flex gap-3 flex-1 overflow-x-auto overflow-y-hidden px-1">
            {kanbanColumns.map(col => {
              const colLeads = getKanbanLeads(col.id);
              const shown = kanbanShownByCol[col.id] ?? LEADS_PAGE_SIZE;
              const visibleLeads = colLeads.slice(0, shown);
              const remaining = Math.max(0, colLeads.length - shown);
              return (
                <div
                  key={col.id}
                  className={`orion-kanban-column rounded-2xl flex flex-col min-w-[200px] flex-1 ${dragOverColumn === col.id ? 'orion-kanban-drop-active' : ''}`}
                  onDragOver={(e) => handleDragOver(e, col.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, col.id)}
                >
                  {/* Header colonne */}
                  <div className="orion-kanban-header rounded-t-2xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }}></div>
                      <span className="text-xs font-black text-[#e0eaff] uppercase tracking-tight">{col.label}</span>
                    </div>
                    <span className="bg-[rgba(100,140,210,0.2)] text-[#7f93bc] text-[10px] font-bold px-2 py-0.5 rounded-md">{colLeads.length}</span>
                  </div>

                  {/* Body scrollable */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ minHeight: '200px' }}>
                    {colLeads.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-10 text-[#3d5280]">
                        <svg className="w-8 h-8 mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                        <span className="text-[10px] font-bold">Aucun lead</span>
                      </div>
                    )}
                    {visibleLeads.map(lead => {
                        const revColor = lead.potentialRevenue ? effectiveRevenueColors[lead.potentialRevenue] : undefined;
                        return (
                        <div
                          key={lead.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, lead.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => setSelectedLead(lead)}
                          className={`orion-lead-card p-3.5 rounded-xl transition-all cursor-grab active:cursor-grabbing relative group/kcard ${draggedLeadId === lead.id ? 'orion-lead-card-dragging' : ''}`}
                          style={{
                            ...(revColor ? { '--rev-border-color': revColor } : {}),
                            ...(cardBg ? { '--card-bg': cardBg } : {}),
                            ...(cardName ? { '--card-name': cardName } : {}),
                            ...(cardEmail ? { '--card-email': cardEmail } : {})
                          } as React.CSSProperties}
                        >
                          {/* Bouton appel rapide */}
                          {cardConfig.showQuickCall && lead.phone && (
                            <a
                              href={`tel:${lead.phone.replace(/\s/g, '')}`}
                              onClick={e => e.stopPropagation()}
                              className="absolute top-2.5 right-2.5 w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-all z-10 opacity-0 group-hover/kcard:opacity-100"
                              title={`Appeler ${lead.name}`}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                            </a>
                          )}

                          {/* En-tête : avatar + nom + ★ favori. L'étoile est
                              groupée avec le nom dans un sous-flex pour rester
                              collée à droite du texte (et non pushed au bord
                              du card où elle chevaucherait le bouton appel). */}
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-black text-white shadow-sm flex-shrink-0" style={{ backgroundColor: col.color }}>
                              {lead.name.charAt(0)}
                            </div>
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                              <div className="text-[13px] font-bold truncate min-w-0" style={{ color: cardName || (isLight ? '#1e293b' : '#e0eaff') }}>{lead.name}</div>
                              {lead.isFavorite && <span className="text-amber-400 text-base leading-none flex-shrink-0" title="Favori">★</span>}
                            </div>
                          </div>

                          {/* Corps en grille positionnée */}
                          {(() => {
                            const cols = cardConfig.gridCols ?? 2;
                            const bodyFields = cardConfig.fields.filter(f =>
                              f.key !== 'name' && f.enabled && f.row !== undefined && f.col !== undefined
                            );
                            if (bodyFields.length === 0) return null;
                            return (
                              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridAutoRows: 'minmax(18px, auto)', gap: '2px 4px' }}>
                                {bodyFields.map(field => {
                                  let content: React.ReactNode = null;
                                  const fk = field.key;
                                  if (fk === 'email') {
                                    content = <div className="text-[10px] truncate" style={{ color: cardEmail || (isLight ? '#64748b' : '#546a96') }}>{displayEmail(lead.email, user.role, company.maskContacts)}</div>;
                                  } else if (fk === 'phone') {
                                    content = lead.phone ? <div className="text-[10px] truncate" style={{ color: cardEmail || (isLight ? '#64748b' : '#546a96') }}>{displayPhone(lead.phone, user.role, company.maskContacts)}</div> : null;
                                  } else if (fk === 'clientCompanyName') {
                                    content = lead.clientCompanyName ? <span className="text-[9px] font-bold bg-[rgba(100,140,210,0.12)] px-1.5 py-0.5 rounded" style={{ color: cardEmail || (isLight ? '#64748b' : '#7f93bc') }}>🏢 {lead.clientCompanyName}</span> : null;
                                  } else if (fk === 'sectorActivity') {
                                    content = lead.sectorActivity ? <span className="text-[9px] font-bold bg-[rgba(100,140,210,0.12)] px-1.5 py-0.5 rounded" style={{ color: cardEmail || (isLight ? '#64748b' : '#7f93bc') }}>{lead.sectorActivity}</span> : null;
                                  } else if (fk === 'status') {
                                    const st = findStatus(statuses, lead.status);
                                    content = st ? <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md border" style={statusBadgeStyle(st.color)}>{st.label}</span> : null;
                                  } else if (fk === 'tag') {
                                    content = lead.tag ? <span className="text-[9px] font-bold text-[#11c5d6] bg-[rgba(17,197,214,0.1)] px-1.5 py-0.5 rounded-md">{lead.tag}</span> : null;
                                  } else if (fk === 'potentialRevenue') {
                                    content = lead.potentialRevenue ? (
                                      <div className="flex items-center gap-1">
                                        {revColor && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: revColor }} />}
                                        <span className="text-[10px] font-black" style={{ color: revColor || (isLight ? '#1e293b' : '#e0eaff') }}>{lead.potentialRevenue}</span>
                                      </div>
                                    ) : null;
                                  } else if (fk === 'createdAt') {
                                    content = <div className="text-[9px] font-bold text-[#3d5280] whitespace-nowrap">{formatDateShort(lead.createdAt)}</div>;
                                  } else if (fk === 'agent') {
                                    content = (
                                      <div className="flex items-center gap-1">
                                        <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${lead.assignedTo}`} className="w-3.5 h-3.5 rounded-full bg-[rgba(100,140,210,0.1)] flex-shrink-0" alt="" />
                                        <span className="text-[9px] font-bold truncate" style={{ color: cardName || (isLight ? '#1e293b' : '#e0eaff') }}>{users.find(u => String(u.id) === String(lead.assignedTo))?.name || '...'}</span>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={fk} className="flex items-center min-h-[18px] min-w-0 overflow-hidden" style={{ gridRowStart: field.row! + 1, gridColumnStart: field.col! + 1, justifyContent: field.align === 'center' ? 'center' : field.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                      {content}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                        );
                    })}
                    {remaining > 0 && (
                      <button
                        type="button"
                        onClick={() => setKanbanShownByCol(prev => ({ ...prev, [col.id]: shown + LEADS_PAGE_SIZE }))}
                        className="w-full text-[10px] font-black uppercase tracking-widest text-[#11c5d6] hover:text-[#7be4ee] bg-[rgba(17,197,214,0.08)] hover:bg-[rgba(17,197,214,0.15)] py-2 rounded-lg transition-all"
                      >
                        + {Math.min(remaining, LEADS_PAGE_SIZE)} de plus
                      </button>
                    )}
                    {shown > LEADS_PAGE_SIZE && (
                      <button
                        type="button"
                        onClick={() => setKanbanShownByCol(prev => ({ ...prev, [col.id]: Math.max(LEADS_PAGE_SIZE, shown - LEADS_PAGE_SIZE) }))}
                        className="w-full text-[10px] font-black uppercase tracking-widest text-[#7f93bc] hover:text-[#c8daf5] bg-[rgba(100,140,210,0.08)] hover:bg-[rgba(100,140,210,0.15)] py-2 rounded-lg transition-all"
                      >
                        Afficher moins
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // VUE LISTE (PROSPECTS) - AVEC TRI ET FILTRE
        <div className="flex flex-col flex-1 min-h-0 gap-3">
        {/* Barre d'actions en masse */}
        {selectedLeadIds.size > 0 && (
          <div className="orion-table-shell flex items-center gap-4 px-5 py-3 rounded-2xl">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-indigo-500 flex items-center justify-center text-white text-[11px] font-black">{selectedLeadIds.size}</div>
              <span className="text-[11px] font-black uppercase tracking-widest text-indigo-400">sélectionné{selectedLeadIds.size > 1 ? 's' : ''}</span>
            </div>
            <div className="h-5 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <SelectField
                value={bulkStatusValue}
                onChange={(v) => { setBulkStatusValue(v); if (v) { handleBulkStatusChange(v); setBulkStatusValue(''); } }}
                options={[
                  { value: '', label: 'Changer statut...' },
                  ...statuses.map(s => ({ value: s.id, label: s.label }))
                ]}
                className="text-xs py-1.5 px-3"
              />
              <SelectField
                value={bulkAssignValue}
                onChange={(v) => { setBulkAssignValue(v); if (v) { handleBulkReassign(v); setBulkAssignValue(''); } }}
                options={[
                  { value: '', label: 'Réassigner à...' },
                  ...visibleAgents.map(u => ({ value: String(u.id), label: u.name }))
                ]}
                className="text-xs py-1.5 px-3"
              />
              <button type="button" onClick={handleBulkDelete} disabled={bulkProcessing} className="orion-lead-select flex items-center gap-1.5 text-[11px] font-black text-rose-400 hover:text-rose-300 py-1.5 px-3 rounded-xl transition-colors disabled:opacity-50">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Supprimer
              </button>
            </div>
            <button type="button" onClick={() => setSelectedLeadIds(new Set())} className="ml-auto p-1.5 rounded-lg text-slate-500 hover:text-slate-300 transition-colors" title="Annuler la sélection">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}
        <div className="orion-table-shell flex-1 rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="overflow-auto custom-scrollbar flex-1 min-h-0">
                <table className="w-full text-left border-collapse">
                    <thead className="orion-table-head sticky top-0 z-10">
                        <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest orion-table-separator">
                            <th className="orion-table-th pl-5 pr-2 py-5 w-12" aria-label="Sélection">
                                <label className="orion-checkbox" title="Tout sélectionner">
                                  <input type="checkbox" checked={paginatedLeads.length > 0 && selectedLeadIds.size === paginatedLeads.length} onChange={toggleSelectAll} />
                                  <span className="orion-checkbox-mark">
                                    <svg viewBox="0 0 12 10" fill="none"><path d="M1 5.5L4 8.5L11 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  </span>
                                </label>
                            </th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('name')}>
                                <div className="flex items-center">Prospect <SortIcon colKey="name" /></div>
                            </th>
                            <th className="orion-table-th px-6 py-5">Téléphone</th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('status')}>
                                <div className="flex items-center">Statut <SortIcon colKey="status" /></div>
                            </th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('revenue')}>
                                <div className="flex items-center">Montant <SortIcon colKey="revenue" /></div>
                            </th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('assignedTo')}>
                                <div className="flex items-center">Assigné à <SortIcon colKey="assignedTo" /></div>
                            </th>
                            <th className="orion-table-th px-6 py-5 cursor-pointer transition-colors" onClick={() => handleSort('createdAt')}>
                                <div className="flex items-center">Date <SortIcon colKey="createdAt" /></div>
                            </th>
                            <th className="px-6 py-5 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="orion-table-body-dividers">
                        {paginatedLeads.map((lead) => {
                        const revColor = lead.potentialRevenue ? effectiveRevenueColors[lead.potentialRevenue] : undefined;
                        return (
                        <tr key={lead.id} onClick={() => setSelectedLead(lead)} className={`orion-table-row-hover cursor-pointer group transition-colors ${selectedLeadIds.has(lead.id) ? 'orion-table-row-selected' : ''}`}
                          style={revColor ? { borderLeft: `3px solid ${revColor}` } : undefined}
                        >
                            <td className="pl-5 pr-2 py-4" onClick={(e) => e.stopPropagation()}>
                                <label className="orion-checkbox" title={`Sélectionner ${lead.name}`}>
                                  <input type="checkbox" checked={selectedLeadIds.has(lead.id)} onChange={() => setSelectedLeadIds(prev => { const n = new Set(prev); n.has(lead.id) ? n.delete(lead.id) : n.add(lead.id); return n; })} />
                                  <span className="orion-checkbox-mark">
                                    <svg viewBox="0 0 12 10" fill="none"><path d="M1 5.5L4 8.5L11 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  </span>
                                </label>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white shadow-sm" style={statusDotStyle(findStatus(statuses, lead.status).color)}>
                                        {lead.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{lead.name}</div>
                                            {lead.isFavorite && <span className="text-amber-400 text-lg leading-none">★</span>}
                                        </div>
                                        <div className="text-xs text-slate-500">{displayEmail(lead.email, user.role, company.maskContacts)}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                {lead.phone ? (
                                  <a
                                    href={`tel:${lead.phone}`}
                                    title={`Appeler ${displayPhone(lead.phone, user.role, company.maskContacts)}`}
                                    aria-label={`Appeler ${displayPhone(lead.phone, user.role, company.maskContacts)}`}
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-indigo-600 hover:text-white hover:bg-indigo-600 transition-colors"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                  </a>
                                ) : (
                                  <span className="text-slate-300" aria-label="Pas de téléphone">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                  </span>
                                )}
                            </td>
                            <td className="px-6 py-4">
                                <span className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border" style={statusBadgeStyle(findStatus(statuses, lead.status).color)}>
                                    {findStatus(statuses, lead.status).label}
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                {lead.potentialRevenue ? (
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black"
                                    style={revColor ? {
                                      backgroundColor: revColor + '18',
                                      color: revColor,
                                      border: `1px solid ${revColor}50`
                                    } : { color: '#475569' }}
                                  >
                                    {revColor && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: revColor }} />}
                                    {lead.potentialRevenue}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-sm font-bold">-</span>
                                )}
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${lead.assignedTo}`} className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200" />
                                    <span className="text-xs font-bold text-slate-600">
                                        {users.find(u => String(u.id) === String(lead.assignedTo))?.name || '...'}
                                    </span>
                                </div>
                            </td>
                            <td className="px-6 py-4 text-xs font-bold text-slate-500">
                                {formatDateShort(lead.createdAt)}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <button type="button" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </td>
                        </tr>
                        );
                        })}
                    </tbody>
                </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
                <span className="text-xs font-bold text-slate-400">
                  {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedLeads.length)} sur {sortedLeads.length}
                </span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'<<'}</button>
                  <button type="button" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'<'}</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page: number;
                    if (totalPages <= 5) { page = i + 1; }
                    else if (currentPage <= 3) { page = i + 1; }
                    else if (currentPage >= totalPages - 2) { page = totalPages - 4 + i; }
                    else { page = currentPage - 2 + i; }
                    return (
                      <button type="button" key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 rounded-lg text-xs font-black transition-colors ${currentPage === page ? 'bg-indigo-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{page}</button>
                    );
                  })}
                  <button type="button" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'>'}</button>
                  <button type="button" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">{'>>'}</button>
                </div>
              </div>
            )}
        </div>
        </div>
      )}

      {selectedLead && (
        <LeadDetailsModal
          key={selectedLead.id}
          lead={selectedLead}
          currentUser={user}
          companyUsers={users}
          company={company}
          currency={company.currency}
          timezone={company.timezone}
          revenueBrackets={company.revenueBrackets}
          maskContacts={company.maskContacts}
          customFieldDefinitions={company.customFieldDefinitions}
          customStatuses={company.customStatuses}
          readOnly={!canModifyLead}
          currentUserCustomRole={currentUserCustomRole}
          onClose={() => setSelectedLead(null)}
          onStatusChange={() => {}}
          onUpdate={handleLeadUpdate}
          onDelete={handleDeleteLead}
        />
      )}

      {showAddLead && (
        company.leadCreationMode === 'full' ? (
          <LeadDetailsModal
            isCreating
            lead={{
              id: '',
              companyId: company.id,
              clientCompanyName: '',
              tag: '',
              sectorActivity: '',
              name: '',
              email: '',
              phone: '',
              phoneLandline: '',
              status: 'new',
              assignedTo: user.id,
              createdAt: new Date().toISOString(),
              additionalInfo: '',
              potentialRevenue: '',
              isFavorite: false,
              source: '',
              customFields: {},
            }}
            currentUser={user}
            companyUsers={users}
            company={company}
            currency={company.currency}
            timezone={company.timezone}
            revenueBrackets={company.revenueBrackets}
            maskContacts={company.maskContacts}
            customFieldDefinitions={company.customFieldDefinitions}
            customStatuses={company.customStatuses}
            onClose={() => setShowAddLead(false)}
            onStatusChange={() => {}}
            onUpdate={() => {}}
            onCreate={async (data) => {
              // handleAddLead lance une erreur en cas d'échec → on relance vers
              // LeadDetailsModal qui affiche un message explicite (doublon, etc.)
              // et reste ouverte. La fermeture n'intervient qu'en succès.
              await handleAddLead(data);
              setShowAddLead(false);
            }}
          />
        ) : (
          <AddLeadModal currentUser={user} revenueBrackets={company.revenueBrackets} customFieldDefinitions={company.customFieldDefinitions} onClose={() => setShowAddLead(false)} onSubmit={handleAddLead} />
        )
      )}

      {importFile && (
        <Suspense fallback={null}>
          <ImportLeadsModal
            file={importFile}
            customFieldDefinitions={company.customFieldDefinitions || []}
            onClose={() => setImportFile(null)}
            onImport={handleImportRows}
          />
        </Suspense>
      )}

      {/* Modale Pipeline — leads par statut */}
      {pipelineModalCol && (() => {
        const colDef = columns.find(c => c.id === pipelineModalCol);
        const colLeads = getLeadsForColumn(pipelineModalCol);
        if (!colDef) return null;
        return (
          <div className="orion-modal-backdrop fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in" onClick={() => setPipelineModalCol(null)}>
            <div className="orion-modal-panel w-full max-w-2xl rounded-[2rem] shadow-2xl border animate-in zoom-in-95 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="p-6 pb-4 border-b border-[rgba(100,140,210,0.15)] flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${colDef.dot}`}></div>
                  <h3 className="text-lg font-black text-white">{colDef.label}</h3>
                  <span className="bg-[rgba(100,140,210,0.2)] text-[#7f93bc] text-xs font-bold px-2.5 py-0.5 rounded-lg">{colLeads.length} leads</span>
                </div>
                <button type="button" title="Fermer" onClick={() => setPipelineModalCol(null)} className="p-2 rounded-xl text-[#546a96] hover:text-white hover:bg-[rgba(100,140,210,0.15)] transition-all">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Liste des leads */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {colLeads.length === 0 ? (
                  <div className="text-center py-10 text-[#546a96] text-sm">Aucun lead dans cette catégorie.</div>
                ) : colLeads.map(lead => (
                  <div
                    key={lead.id}
                    onClick={() => { setPipelineModalCol(null); setSelectedLead(lead); }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.1)] hover:border-[rgba(17,197,214,0.3)] hover:bg-[rgba(17,197,214,0.04)] cursor-pointer transition-all"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white flex-shrink-0 ${colDef.dot}`}>{lead.name.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#e0eaff] truncate">{lead.name}</div>
                      <div className="text-[11px] text-[#546a96] truncate">{displayEmail(lead.email, user.role, company.maskContacts)}{lead.phone ? ` · ${displayPhone(lead.phone, user.role, company.maskContacts)}` : ''}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {lead.clientCompanyName && <span className="text-[9px] font-bold text-[#7f93bc] bg-[rgba(100,140,210,0.12)] px-1.5 py-0.5 rounded">{lead.clientCompanyName}</span>}
                        {lead.sectorActivity && <span className="text-[9px] font-bold text-[#7f93bc] bg-[rgba(100,140,210,0.12)] px-1.5 py-0.5 rounded">{lead.sectorActivity}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex items-center gap-1.5">
                        <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${lead.assignedTo}`} className="w-5 h-5 rounded-full bg-[rgba(100,140,210,0.1)]" alt="" />
                        <span className="text-[10px] font-bold text-[#546a96] max-w-[70px] truncate">{users.find(u => String(u.id) === String(lead.assignedTo))?.name || '...'}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] font-black text-[#e0eaff]">{lead.potentialRevenue || '-'}</div>
                        <div className="text-[9px] font-bold text-[#3d5280]">{formatDateShort(lead.createdAt)}</div>
                      </div>
                      <svg className="w-4 h-4 text-[#546a96]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CompanyDashboard;
